<mat-toolbar class="action-toolbar ai-feature">
    <button mat-icon-button (click)="returnToParent()" matTooltip="Back">
        <mat-icon>arrow_back_ios</mat-icon>
    </button>
    <h3>{{ patient | patientHeading:'AI Request Details':'AI Request Details' }}</h3>
    <div>
        <button mat-icon-button *ngIf="patient?.id" appWhatsappDialogTrigger [patientId]="patient?.id"
            [patient]="patient" matTooltip="Open WhatsApp to send message to patient or colleague">
            <mat-icon svgIcon="whatsapp"></mat-icon>
        </button>
        <button mat-icon-button *ngIf="editingRequest && editingRequest.id && userRole !== 'Support-Limited'"
            (click)="deleteRequest()" matTooltip="Delete AI Request">
            <mat-icon color="warn">delete</mat-icon>
        </button>
        <button mat-icon-button *ngIf="userRole !== 'Support-Limited'"
            [disabled]="notesForm.invalid || diagnosisForm.invalid" (click)="saveRequest()"
            matTooltip="Save AI Request">
            <mat-icon>save</mat-icon>
        </button>
    </div>
</mat-toolbar>
<div id="top"></div>

<ng-template #notAuthorized>
    <div class="main-container">
        <mat-card class="unauthorized-card">
            <mat-card-content>
                <mat-icon color="warn">error_outline</mat-icon>
                <h2><strong>Unauthorized</strong></h2>
                <p class="hint">
                    You are not authorized to view this page. Please contact the
                    group administrator to request access.
                </p>
            </mat-card-content>
        </mat-card>
    </div>
</ng-template>

<div class="main-container" *ngIf="userRole !== 'Support-Limited'; else notAuthorized"
    [appFindReplaceScope]="findReplaceTargets" findReplaceScopeName="this AI request">
    <div class="content-container" [class.dynamic-layout]="useDynamicLayout">

        <!-- C4 (§7) — the coding divergence banner, BOTH DIRECTIONS.
             It appears only after a procedure note has already been saved to the timeline, and
             it is dismissible. It warns; it never blocks, and there is no path from here back
             into the save. The second list is the one nothing catches today: work that was
             planned and authorised and then not captured is straight revenue loss. Modifiers
             are classified server-side and are NOT counted as unplanned — a banner that fires
             on every modifier is a banner nobody reads. -->
        <div class="coding-divergence-banner" *ngIf="divergence as divergence">
            <mat-icon>rule</mat-icon>
            <div class="divergence-body">
                <p class="divergence-title"><strong>The codes captured differ from the booking.</strong>
                    The note has been saved — this is a warning, not a block.</p>
                <!-- §3d — the same honesty the pre-save block owes. When the booking was matched
                     BY DATE rather than by a link the user made, say so: a reader who can see how
                     we got to a booking can tell us it is the wrong one. Nothing was written back
                     against an inferred booking. -->
                <p *ngIf="divergence.inferred">
                    <em>Matched to a theatre booking around this note's date. If that is the wrong
                        case, ignore this.</em>
                </p>
                <p *ngIf="divergence.warnings?.capturedNotPlanned?.length">
                    <strong>Captured but not planned:</strong>
                    <span *ngFor="let code of divergence.warnings.capturedNotPlanned; let last = last">
                        {{ code.code }}{{ code.description ? ' — ' + code.description : '' }}{{ last ? '' : ', ' }}
                    </span>
                </p>
                <p *ngIf="divergence.warnings?.plannedNotCaptured?.length">
                    <strong>Planned but not captured:</strong>
                    <span *ngFor="let code of divergence.warnings.plannedNotCaptured; let last = last">
                        {{ code.code }}{{ code.description ? ' — ' + code.description : '' }}{{ last ? '' : ', ' }}
                    </span>
                    <em> — the work was booked and authorised; check whether a line was missed.</em>
                </p>
            </div>
            <button mat-icon-button (click)="dismissDivergenceBanner()" matTooltip="Dismiss">
                <mat-icon>close</mat-icon>
            </button>
        </div>

        <div class="ai-locked" *ngIf="lockProcessing && !isPremium">
            <mat-icon>error</mat-icon>
            <p class="p-l-10">You do not have enough AI tokens to continue utilizing AI features. Please recharge your
                token
                balance by navigating to settings and selecting the "Subscriptions & Usage" tab.</p>
        </div>
        <ng-container *ngIf="!lockProcessing">
            <div class="token-balance-warning" *ngIf="!isPremium && tokenBalance && tokenBalance <= 100">
                <mat-icon>warning</mat-icon>
                <p class="p-l-10">Your token balance is currently R{{ tokenBalance }}. Please
                    recharge soon to ensure you are able to continue utilizing AI features.</p>
            </div>
            <mat-accordion multi="true" *ngIf="patient">
                <form [formGroup]="notesForm">

                    <mat-expansion-panel [expanded]="false" class="darkest"
                        *ngIf="useDynamicLayout ? visibleSections.details : true">
                        <mat-expansion-panel-header>
                            <mat-panel-title>REQUEST DETAILS</mat-panel-title>
                        </mat-expansion-panel-header>

                        <mat-form-field class="full-width" appearance="outline">
                            <mat-label>Request Title</mat-label>
                            <input matInput placeholder="Request Title" formControlName="suggestedNoteTitle">
                        </mat-form-field>

                        <div class="full-width field-row">

                            <mat-form-field appearance="outline" class="fill">
                                <mat-label>Start Time</mat-label>
                                <input matInput formControlName="startTime" [ngxTimepicker]="startTimePicker"
                                    [format]="24">
                                <ngx-material-timepicker #startTimePicker [theme]="darkTheme" [appendToInput]="true"
                                    [preventOverlayClick]="true"></ngx-material-timepicker>
                            </mat-form-field>


                            <mat-form-field appearance="outline" class="fill">
                                <mat-label>Finish Time</mat-label>
                                <input matInput formControlName="finishTime" [ngxTimepicker]="finishTimePicker"
                                    [format]="24">
                                <ngx-material-timepicker #finishTimePicker [theme]="darkTheme" [appendToInput]="true"
                                    [preventOverlayClick]="true"></ngx-material-timepicker>
                            </mat-form-field>
                        </div>

                    </mat-expansion-panel>

                    <mat-expansion-panel [expanded]="isExpanded('notes')" id="notes"
                        *ngIf="useDynamicLayout ? visibleSections.notes : true">
                        <mat-expansion-panel-header>
                            <mat-panel-title>NOTES</mat-panel-title>
                        </mat-expansion-panel-header>

                        <app-allergy-list-creator [allergies]="selectedAllergies" [noAllergies]="this.noAllergies"
                            (allergiesChanged)="onPatientAllergiesChanged($event)"
                            (noAllergiesChanged)="onNoAllergiesChanged($event)" heading="Existing Allergies / Alerts"
                            *ngIf="allergies?.length && !hideAllergies"></app-allergy-list-creator>


                        <div class="allergy-alert" *ngIf="allergies?.length && !hideAllergies">
                            <mat-icon>warning</mat-icon>
                            <h3>Allergies Detected</h3>
                            <ng-container *ngFor="let allergy of allergies">
                                <div class="allergy">
                                    <span>{{allergy.allergy}} - {{allergy.reaction}}</span>
                                    <button mat-icon-button (click)="addAllergy($event, allergy)"
                                        *ngIf="canAddAllergy(allergy.allergy)"
                                        matTooltip="Add this allergy to the patient file">
                                        <mat-icon>library_add</mat-icon>
                                    </button>
                                </div>
                            </ng-container>
                            <!--  <button class="hide-allergies" mat-icon-button (click)="hideAllergiesBanner()"
                                matTooltip="Hide these allergy warnings">
                                <mat-icon>close</mat-icon>
                            </button> -->
                        </div>



                        <!-- <mat-form-field appearance="outline" class="full-width">
                            <mat-label>Detected Note Type</mat-label>
                            <mat-select formControlName="noteTypeId" (selectionChange)="aiRegenerateNotes($event)">
                                <mat-option *ngFor="let noteType of noteTypes" [value]="noteType.id">
                                    {{ noteType.label }}
                                </mat-option>
                            </mat-select>
                        </mat-form-field> -->

                        <mat-form-field appearance="outline" class="full-width">
                            <mat-label>Detected Note Type</mat-label>
                            <mat-select formControlName="noteTypeId" (selectionChange)="aiRegenerateNotes($event)">

                                <!-- CONSULT Group -->
                                <mat-optgroup label="--- CONSULT NOTES ---">
                                    <mat-option *ngFor="let noteType of consultNoteTypes" [value]="noteType.id">
                                        {{ noteType.label }}
                                    </mat-option>
                                </mat-optgroup>

                                <!-- PROCEDURE Group -->
                                <mat-optgroup label="--- PROCEDURE NOTES ---">
                                    <mat-option *ngFor="let noteType of procedureNoteTypes" [value]="noteType.id">
                                        {{ noteType.label }}
                                    </mat-option>
                                </mat-optgroup>

                            </mat-select>
                        </mat-form-field>

                        <mat-form-field appearance="outline" class="full-width">
                            <mat-label>Notes</mat-label>
                            <textarea matInput appFindReplace cdkTextareaAutosize="false" rows="20" formControlName="notes"
                                #notesTextarea></textarea>
                        </mat-form-field>

                        <mat-form-field appearance="outline" class="full-width">
                            <textarea matInput placeholder="Instruct MedicAI" cdkTextareaAutosize="true" rows="1"
                                #notesAutosize="cdkTextareaAutosize" formControlName="notesPrompt"
                                (keydown.enter)="aiPerformUserPrompt('notes', $event)"></textarea>
                            <button matSuffix mat-icon-button (click)="aiPerformUserPrompt('notes')">
                                <mat-icon>send</mat-icon>
                            </button>
                            <mat-hint>
                                Customize your output
                                <mat-icon
                                    matTooltip="You can say things like, output as paragraphs, or add additional information to X heading.">info</mat-icon>
                            </mat-hint>
                        </mat-form-field>

                        <div class="information-banner" *ngIf="!hasTimlineData('notes')">
                            <mat-icon>info</mat-icon>
                            <p class="p-l-10">Remember to validate your notes to the patient's timeline. When validating
                                you will
                                be able to manage the tariff codes for this interaction</p>
                        </div>

                        <!-- <mat-form-field appearance="outline" class="m-t-20 full-width">
                            <mat-label>Detected Coding Bundle</mat-label>
                            <mat-select formControlName="codingBundleId" (selectionChange)="selectCodingBundle($event)">
                                <mat-option *ngFor="let bundle of codingBundles" [value]="bundle.id">
                                    {{ bundle.label }}
                                </mat-option>
                            </mat-select>
                        </mat-form-field>

                        <div class="icd-container " id="pc">
                            <h3>Selected Procedure Codes</h3>

                            <div class="toggle-container" *ngIf="!stateUnit">
                                <mat-slide-toggle formControlName="paidCash" color="primary"
                                    (change)="togglePaidCash($event)">Paid Up
                                    Front</mat-slide-toggle>
                                <mat-slide-toggle formControlName="noCharge" color="primary"
                                    (change)="toggleNoCharge($event)" class="m-l-5">Pro Bono</mat-slide-toggle>
                            </div>

                            <app-procedure-code-selector #procedureCodeSelector [initialCodes]="selectedProcedureCodes"
                                (codesChanged)="onSelectedProcedureCodesChanged($event, null)"
                                [providedStartTime]="notesForm.get('startTime')?.value"
                                [providedFinishTime]="notesForm.get('finishTime')?.value"
                                [patient]="patient"></app-procedure-code-selector>
                        </div>


                        <div class="icd-container m-t-10">
                            <ng-container *ngIf="notesForm.get('suggestedProcedureCodes')?.value?.codes">
                                <h4>Suggested Procedure Codes</h4>
                                <mat-list class="full-width">
                                    <mat-list-item class="auto-height"
                                        *ngFor="let code of notesForm.get('suggestedProcedureCodes')?.value?.codes">
                                        <div class="icd-item">
                                            <div class="icd-details">
                                                <p matLine class="icd-title">{{code.code}} - {{code.description}}</p>
                                                <p matLine class="icd-score">Relevance: {{code.score}}%</p>
                                            </div>
                                            <div class="view-icon">
                                                <button mat-icon-button [matTooltip]="code.reason">
                                                    <mat-icon>info</mat-icon>
                                                </button>
                                                <button mat-icon-button color="primary"
                                                    matTooltip="Add this code to the patient file"
                                                    (click)="addProcedureCode($event, code)">
                                                    <mat-icon>library_add</mat-icon>
                                                </button>
                                            </div>
                                        </div>
                                    </mat-list-item>
                                </mat-list>
                            </ng-container>

                            <div class="actions wrap">
                                <ng-container *ngIf="!notesForm.get('suggestedProcedureCodes')?.value?.codes?.length">
                                    <button mat-raised-button class="ai-feature"
                                        (click)="aiGenerateProcedureCodeSuggestions()"
                                        matTooltip="Generate a list of Procedure Code suggestions using MedicAI">
                                        <mat-icon svgIcon="medic_ai"></mat-icon> Generate
                                    </button>
                                </ng-container>
                            </div>
                        </div> -->

                        <div class="actions wrap">

                            <ng-container *ngIf="canSign; else cantSign">
                                <button mat-raised-button color="secondary" [matMenuTriggerFor]="downloadMenu">
                                    <mat-icon>file_download</mat-icon> Download
                                </button>
                                <mat-menu #downloadMenu="matMenu" class="p-10">
                                    <mat-checkbox formControlName="includeSignatureOnPdf" class="checkbox"
                                        (click)="$event.stopPropagation()">
                                        {{isPhysician ?'Attach Signature' : 'Attach Doctor\'s Signature'}}
                                    </mat-checkbox>
                                    <mat-form-field *ngIf="hasSignaturePermission" appearance="outline"
                                        class="full-width" (keydown)="handleMenuKeydown($event)"
                                        (click)="$event.stopPropagation()">
                                        <mat-label>Select Doctor's Signature</mat-label>
                                        <mat-select formControlName="selectedDoctor">
                                            <mat-option *ngFor="let doctor of groupUsers" [value]="doctor">
                                                {{doctor.title}} {{ doctor.name }} {{doctor.surname}}
                                            </mat-option>
                                        </mat-select>
                                    </mat-form-field>

                                    <mat-form-field appearance="outline" class="full-width"
                                        (keydown)="handleMenuKeydown($event)" (click)="$event.stopPropagation()">
                                        <mat-label>Document Date</mat-label>
                                        <input matInput [matDatepicker]="pdfDatePicker_notes" formControlName="pdfDate" placeholder="DD/MM/YYYY">
                                        <mat-datepicker-toggle matSuffix [for]="pdfDatePicker_notes"></mat-datepicker-toggle>
                                        <mat-datepicker #pdfDatePicker_notes></mat-datepicker>
                                    </mat-form-field>

                                    <button mat-raised-button color="primary" class="full-width"
                                        (click)="downloadAsPdf('notes')">
                                        Download PDF <mat-icon>file_download</mat-icon>
                                    </button>
                                </mat-menu>
                            </ng-container>
                            <ng-template #cantSign>
                                <button mat-raised-button color="primary" (click)="downloadAsPdf('notes')">
                                    <mat-icon>file_download</mat-icon> Download PDF
                                </button>
                            </ng-template>
                            <ng-container *ngIf="hasTimlineData('notes')">
                                <span class="status-message green"><mat-icon>check_circle</mat-icon> Note exists in
                                    timeline</span>
                            </ng-container>
                            <button mat-raised-button color="primary" (click)="createPatientNote('notes')"
                                matTooltip="Create a note in the clinical timeline using this information">
                                <mat-icon>library_add</mat-icon> Validate To Timeline
                            </button>
                        </div>
                    </mat-expansion-panel>

                    <mat-expansion-panel [expanded]="isExpanded('file_update')" class="darkest" id="file_update"
                        *ngIf="useDynamicLayout ? visibleSections.fileUpdate : true">
                        <mat-expansion-panel-header>
                            <mat-panel-title>FILE UPDATE</mat-panel-title>
                        </mat-expansion-panel-header>

                        <form [formGroup]="diagnosisForm">

                            <div class="field-with-action">
                                <mat-form-field appearance="outline">
                                    <mat-label>Diagnosis/Active Problem</mat-label>
                                    <textarea matInput cdkTextareaAutosize="true"
                                        formControlName="diagnosis"></textarea>
                                    <mat-error *ngIf="diagnosisForm.get('diagnosis')?.invalid">Please
                                        enter a primary
                                        diagnosis/active problem</mat-error>
                                </mat-form-field>
                                <button mat-icon-button matTooltip="Add additional diagnosis"
                                    (click)="addDiagnosisField()"
                                    *ngIf="visibleDiagnosisFields.length <= 24"><mat-icon>add</mat-icon></button>
                            </div>

                            <ng-container *ngFor="let field of visibleDiagnosisFields; let i = index">
                                <mat-form-field appearance="outline" class="full-width">
                                    <mat-label>Diagnosis/Active Problem {{ i + 2 }}</mat-label>
                                    <textarea matInput cdkTextareaAutosize="true" [formControlName]="field"></textarea>
                                </mat-form-field>
                            </ng-container>

                            <div class="full-width field-row">

                                <mat-form-field appearance="outline" class="fill">
                                    <mat-label>Height</mat-label>
                                    <input matInput formControlName="height" type="number" heightInput>
                                    <span matSuffix class="m-r-5"> cm </span>
                                </mat-form-field>

                                <mat-form-field appearance="outline" class="fill">
                                    <mat-label>Weight</mat-label>
                                    <input matInput formControlName="weight" type="number" weightInput>
                                    <span matSuffix class="m-r-5"> kg </span>
                                </mat-form-field>

                            </div>

                        </form>

                        <div class="icd-container">
                            <h3>Current Patient ICD-10 Codes</h3>
                            <app-icd-code-selector [patientSex]="icdCodingSex" [patientAge]="icdCodingAge" [defaultOnsetDate]="icdDefaultOnsetDate" [initialCodes]="selectedPatientIcdCodes"
                                (codesChanged)="onPatientIcdCodesChanged($event)"
                                #icdCodeSelector></app-icd-code-selector>
                        </div>

                        <div class="icd-container" *ngIf="notesForm.get('suggestedICD10Codes')?.value?.codes">
                            <mat-list class="full-width">
                                <mat-list-item class="auto-height"
                                    *ngFor="let code of notesForm.get('suggestedICD10Codes')?.value?.codes">
                                    <div class="icd-item">
                                        <div class="icd-details">
                                            <p matLine class="icd-title">{{code.code}} - {{code.description}}</p>
                                            <p matLine class="icd-score">Relevance: {{code.score}}%</p>
                                        </div>
                                        <div class="view-icon">
                                            <button mat-icon-button [matTooltip]="code.reason">
                                                <mat-icon>info</mat-icon>
                                            </button>
                                            <button mat-icon-button color="primary"
                                                matTooltip="Add this code to the patient file"
                                                (click)="addICD10Code($event, code)">
                                                <mat-icon>library_add</mat-icon>
                                            </button>
                                        </div>
                                    </div>
                                </mat-list-item>
                            </mat-list>
                        </div>

                        <app-allergy-list-creator [allergies]="selectedAllergies" [noAllergies]="noAllergies"
                            (allergiesChanged)="onPatientAllergiesChanged($event)"
                            (noAllergiesChanged)="onNoAllergiesChanged($event)"></app-allergy-list-creator>

                        <app-medical-history-list-creator [history]="selectedMedicalHistory"
                            [noHistory]="noMedicalHistory" (historyChanged)="onPatientMedicalHistoryChanged($event)"
                            (noHistoryChanged)="onNoMedicalHistoryChanged($event)">
                        </app-medical-history-list-creator>

                        <div class="icd-container" *ngIf="notesForm.get('detectedMedicalHistory')?.value">
                            <mat-list class="full-width">
                                <mat-list-item class="auto-height"
                                    *ngFor="let history of notesForm.get('detectedMedicalHistory')?.value">
                                    <div class="icd-item">
                                        <div class="icd-details">
                                            <p matLine class="icd-title">{{history.surgery}} - {{history.year}}</p>
                                            <p matLine class="icd-score">Relevance: {{history.score}}%</p>
                                        </div>
                                        <div class="view-icon">
                                            <button mat-icon-button [matTooltip]="history.reason">
                                                <mat-icon>info</mat-icon>
                                            </button>
                                            <button mat-icon-button color="primary"
                                                matTooltip="Add this event/operation to the patient file"
                                                (click)="addMedicalHistory($event, history)">
                                                <mat-icon>library_add</mat-icon>
                                            </button>
                                        </div>
                                    </div>
                                </mat-list-item>
                            </mat-list>
                        </div>

                        <app-medication-list-creator [medications]="selectedMedicationHistory"
                            [noMedication]="noMedicationHistory"
                            (medicationChanged)="onPatientMedicationHistoryChanged($event)"
                            (noMedicationChanged)="onNoMedicationHistoryChanged($event)">
                        </app-medication-list-creator>

                        <div class="icd-container" *ngIf="notesForm.get('detectedMedications')?.value">
                            <mat-list class="full-width">
                                <mat-list-item class="auto-height"
                                    *ngFor="let medication of notesForm.get('detectedMedications')?.value">
                                    <div class="icd-item">
                                        <div class="icd-details">
                                            <p matLine class="icd-title">{{medication.medication}} -
                                                {{medication.dosage}}</p>
                                            <p matLine class="icd-score">Relevance: {{medication.score}}%</p>
                                        </div>
                                        <div class="view-icon">
                                            <button mat-icon-button [matTooltip]="medication.reason">
                                                <mat-icon>info</mat-icon>
                                            </button>
                                            <button mat-icon-button color="primary"
                                                matTooltip="Add this medication to the patient file"
                                                (click)="addMedication($event, medication)">
                                                <mat-icon>library_add</mat-icon>
                                            </button>
                                        </div>
                                    </div>
                                </mat-list-item>
                            </mat-list>
                        </div>

                    </mat-expansion-panel>

                    <mat-expansion-panel [expanded]="isExpanded('medication')" id="medication"
                        [appGroupSpecialtyHide]="{ specialties: ['Physiotherapy'], condition: 'exclude' }"
                        *ngIf="useDynamicLayout ? visibleSections.medication : true">
                        <mat-expansion-panel-header>
                            <mat-panel-title>MEDICATION</mat-panel-title>
                        </mat-expansion-panel-header>

                        <form [formGroup]="medicationForm">
                            <div class="icd-container">
                                <app-medication-autocomplete [patient]="patient"
                                    #medicationAutocomplete></app-medication-autocomplete>

                                <div class="actions m-t-10" *ngIf="medicationAutocomplete?.isValid()">
                                    <ng-container *ngIf="canSign; else cantSign">
                                        <button mat-raised-button [disabled]="!medicationAutocomplete?.isValid()"
                                            color="primary" [matMenuTriggerFor]="save_prescription">
                                            <mat-icon>save</mat-icon> Save Prescription </button>

                                        <mat-menu #save_prescription="matMenu" class="p-10">
                                            <mat-checkbox formControlName="includeSignatureOnPdf" class="checkbox"
                                                (click)="$event.stopPropagation()">
                                                {{isPhysician ? 'Attach Signature' : 'Attach Doctor\'s Signature'}}
                                            </mat-checkbox>
                                            <mat-form-field *ngIf="hasSignaturePermission" appearance="outline"
                                                class="full-width" (keydown)="handleMenuKeydown($event)"
                                                (click)="$event.stopPropagation()">
                                                <mat-label>Select Doctor's Signature</mat-label>
                                                <mat-select formControlName="selectedDoctor">
                                                    <mat-option *ngFor="let doctor of groupUsers" [value]="doctor">
                                                        {{doctor.title}} {{ doctor.name }} {{doctor.surname}}
                                                    </mat-option>
                                                </mat-select>
                                            </mat-form-field>
                                            <button mat-raised-button color="primary" class="full-width"
                                                (click)="savePrescription()">
                                                Save Prescription <mat-icon>save</mat-icon>
                                            </button>
                                        </mat-menu>
                                    </ng-container>
                                    <ng-template #cantSign>
                                        <button mat-raised-button color="primary" class="full-width"
                                            (click)="savePrescription()">
                                            Save Prescription <mat-icon>save</mat-icon>
                                        </button>
                                    </ng-template>
                                    <!-- A state unit sends nothing (tasks/state-mode.md): Save Prescription above only. -->
                                    <ng-container *ngIf="!stateUnit">
                                    <ng-container *ngIf="canSign; else cantSign">
                                        <button mat-raised-button color="primary"
                                            matTooltip="Save this prescription and send it via email"
                                            [matMenuTriggerFor]="send_prescription"
                                            [disabled]="!medicationAutocomplete?.isValid()">
                                            <mat-icon>mail</mat-icon> Save & Send
                                        </button>
                                        <mat-menu #send_prescription="matMenu" class="p-10">
                                            <div mat-menu-content (click)="$event.stopPropagation()">
                                                <mat-checkbox formControlName="includeSignatureOnEmail" class="checkbox"
                                                    (click)="$event.stopPropagation()">
                                                    {{isPhysician ?'Attach Signature' : 'Attach Doctor\'s Signature'}}
                                                </mat-checkbox>
                                                <mat-form-field *ngIf="hasSignaturePermission" appearance="outline"
                                                    class="full-width" (keydown)="handleMenuKeydown($event)"
                                                    (click)="$event.stopPropagation()">
                                                    <mat-label>Select Doctor's Signature</mat-label>
                                                    <mat-select formControlName="selectedDoctor">
                                                        <mat-option *ngFor="let doctor of groupUsers" [value]="doctor">
                                                            {{doctor.title}} {{ doctor.name }} {{doctor.surname}}
                                                        </mat-option>
                                                    </mat-select>
                                                </mat-form-field>
                                                <mat-form-field appearance="outline" class="full-width"
                                                    (keydown)="handleMenuKeydown($event)">
                                                    <mat-label>Email</mat-label>
                                                    <input matInput formControlName="email">
                                                </mat-form-field>
                                                <mat-form-field appearance="outline" class="full-width">
                                                    <mat-label>CC Email</mat-label>
                                                    <input matInput formControlName="ccEmail">
                                                </mat-form-field>
                                                <button mat-raised-button color="primary" class="full-width"
                                                    [disabled]="!medicationForm.get('email')?.value || medicationForm.get('email')?.invalid"
                                                    (click)="emailPrescription()"
                                                    matTooltip="Send to this email address">
                                                    <mat-icon>send</mat-icon> Send Email
                                                </button>
                                            </div>
                                        </mat-menu>
                                    </ng-container>
                                    <ng-template #cantSign>
                                        <button mat-raised-button color="primary" class="full-width"
                                            [disabled]="!medicationForm.get('email')?.value || medicationForm.get('email')?.invalid"
                                            (click)="emailPrescription()" matTooltip="Send to this email address">
                                            <mat-icon>send</mat-icon> Send Email
                                        </button>
                                    </ng-template>
                                    </ng-container>
                                </div>
                                <div *ngIf="medicationForm.get('suggestedMedication')?.value?.medication"
                                    class="m-t-10">
                                    <h4 class="m-t-10">Suggested Medication</h4>
                                    <mat-list class="full-width">
                                        <mat-list-item class="auto-height"
                                            *ngFor="let suggestion of medicationForm.get('suggestedMedication')?.value?.medication">
                                            <div class="icd-item">
                                                <div class="icd-details">
                                                    <p matLine class="icd-title">{{suggestion.item.name}}</p>
                                                    <p matLine class="icd-score">Relevance: {{suggestion.score}}%</p>
                                                </div>
                                                <div class="view-icon">
                                                    <button mat-icon-button [matTooltip]="suggestion.reason">
                                                        <mat-icon>info</mat-icon>
                                                    </button>
                                                    <button mat-icon-button color="primary"
                                                        *ngIf="canAddMedication(suggestion.item.name)"
                                                        matTooltip="Add this medication to the discharge prescription"
                                                        (click)="addMedicationToPrescription($event, suggestion.item)">
                                                        <mat-icon>library_add</mat-icon>
                                                    </button>
                                                </div>
                                            </div>
                                        </mat-list-item>
                                    </mat-list>
                                </div>
                            </div>
                        </form>

                    </mat-expansion-panel>


                </form>

                <form [formGroup]="labTestsForm">

                    <mat-expansion-panel [expanded]="isExpanded('labTests')" id="labTests" class="darkest"
                        *ngIf="!stateUnit && (useDynamicLayout ? visibleSections.labTests : true)">
                        <mat-expansion-panel-header>
                            <mat-panel-title>LAB TESTS</mat-panel-title>
                        </mat-expansion-panel-header>

                        <div class="icd-container lab-order-form">
                            <div class="m-t-10">
                                <!-- Physician Selection (only for non-doctor users) - Show above warning banner -->
                                <mat-form-field appearance="outline" class="full-width"
                                    *ngIf="isNonDoctorUser && labOrderGroupUsers.length > 0">
                                    <mat-label>Requesting Physician</mat-label>
                                    <mat-select [value]="selectedLabOrderPhysicianId"
                                        (selectionChange)="onLabOrderPhysicianSelectionChanged($event.value)">
                                        <mat-option *ngFor="let user of labOrderGroupUsers" [value]="user.userId">
                                            {{user.title}} {{user.name}} {{user.surname}}<span *ngIf="user.lancetCode">
                                                ✓</span>
                                        </mat-option>
                                    </mat-select>
                                </mat-form-field>

                                <!-- Warning banner if lancetCode is missing -->
                                <mat-card *ngIf="!canSubmitLabOrder" class="warning-banner-card">
                                    <mat-card-content>
                                        <div class="warning-banner">
                                            <mat-icon>warning</mat-icon>
                                            <div class="warning-content">
                                                <strong>Lab Order Submission Disabled</strong>
                                                <p *ngIf="!isNonDoctorUser">Lab orders require a Lancet Code for your
                                                    user profile.</p>
                                                <p *ngIf="isNonDoctorUser && selectedLabOrderPhysician">
                                                    Lab orders require a Lancet Code for <strong>{{
                                                        selectedLabOrderPhysician.title }} {{
                                                        selectedLabOrderPhysician.name }} {{
                                                        selectedLabOrderPhysician.surname }}</strong>
                                                </p>
                                                <div>
                                                    <p *ngIf="!isNonDoctorUser">Please configure your user Lancet Code.
                                                    </p>
                                                    <p *ngIf="isNonDoctorUser && selectedLabOrderPhysician">Please
                                                        configure the Lancet Code for the selected physician.</p>
                                                    <p><a href="#" (click)="openLabOrderConfigDialog($event)">Click here
                                                            to configure now</a></p>
                                                </div>
                                            </div>
                                        </div>
                                    </mat-card-content>
                                </mat-card>

                                <!-- Lab Order Form Fields - Only visible when canSubmitLabOrder is true -->
                                <div *ngIf="canSubmitLabOrder">

                                    <!-- Form fields - only show if physician is selected (or user is a doctor) -->
                                    <div *ngIf="hasSelectedLabOrderPhysician">
                                        <div class="form-row">
                                            <mat-form-field appearance="outline">
                                                <mat-label>Order Date</mat-label>
                                                <input matInput [matDatepicker]="orderDatePicker"
                                                    formControlName="orderDate" placeholder="DD/MM/YYYY">
                                                <mat-datepicker-toggle matSuffix
                                                    [for]="orderDatePicker"></mat-datepicker-toggle>
                                                <mat-datepicker #orderDatePicker></mat-datepicker>
                                            </mat-form-field>

                                            <mat-form-field appearance="outline">
                                                <mat-label>Priority</mat-label>
                                                <mat-select formControlName="priority">
                                                    <mat-option *ngFor="let priority of labOrderPriorities"
                                                        [value]="priority.id">
                                                        {{ priority.label }}
                                                    </mat-option>
                                                </mat-select>
                                            </mat-form-field>
                                        </div>

                                        <mat-form-field appearance="outline" class="full-width">
                                            <mat-label>Notes</mat-label>
                                            <textarea matInput formControlName="labOrderNotes" rows="3"
                                                placeholder="Additional notes for the lab order..."></textarea>
                                        </mat-form-field>

                                        <!-- Lab Test Search Section - Always Visible -->
                                        <div class="lab-test-search-section">
                                            <h4>Select Lab Tests</h4>

                                            <!-- Select Bundles -->
                                            <div class="bundles-subsection" *ngIf="labTestBundles.length > 0">
                                                <mat-form-field appearance="outline" class="full-width m-b-0">
                                                    <mat-label>Select Bundles</mat-label>
                                                    <mat-select multiple formControlName="selectedBundles"
                                                        (selectionChange)="onBundleSelectionChanged($event.value)">
                                                        <mat-option *ngFor="let bundle of labTestBundles"
                                                            [value]="bundle">
                                                            {{ bundle.label }} ({{ bundle.lab || 'Lancet' }})
                                                        </mat-option>
                                                    </mat-select>
                                                </mat-form-field>
                                            </div>

                                            <!-- No Bundles Available Message -->
                                            <div class="no-bundles-message" *ngIf="labTestBundles.length === 0">
                                                <mat-card>
                                                    <mat-card-content>
                                                        <p>No lab test bundles are available. Please configure your
                                                            preferred lab test bundles in
                                                            your profile settings.</p>
                                                    </mat-card-content>
                                                </mat-card>
                                            </div>

                                            <!-- Search Lab Tests -->
                                            <div class="search-subsection">
                                                <app-lab-test-selector [patient]="patient"
                                                    [checklistItems]="selectedLabTests" [mode]="'checklist'"
                                                    [showChecklist]="true" [showCategoryFilter]="false"
                                                    [showLabProvider]="false" [disableAdding]="false"
                                                    [pluralTitle]="'Lab Tests'" [singularTitle]="'Lab Test'"
                                                    (checkedItemsChanged)="onLabTestsChanged($event)"
                                                    (checkedTestsChanged)="onLabTestsSelectionChanged($event)">
                                                </app-lab-test-selector>
                                            </div>
                                        </div>

                                        <!-- Other Available Tests Section -->
                                        <div class="other-tests-section"
                                            *ngIf="showIndividualTests && selectedLabTests.length > 0 && allOtherTests.length > 0">
                                            <h4>Other Favourite Tests</h4>
                                            <app-lab-test-selector [patient]="patient" [checklistItems]="allOtherTests"
                                                [mode]="'checklist'" [showChecklist]="true" [showSearch]="false"
                                                [showCategoryFilter]="false" [showLabProvider]="false"
                                                [disableAdding]="false" [pluralTitle]="'Lab Tests'"
                                                [singularTitle]="'Lab Test'"
                                                (checkedItemsChanged)="onOtherTestsChanged($event)"
                                                (checkedTestsChanged)="onOtherTestsSelectionChanged($event)">
                                            </app-lab-test-selector>
                                        </div>

                                        <!-- Lab Order Submit Button -->
                                        <div class="lab-order-actions m-t-20"
                                            *ngIf="hasSelectedLabTests() || hasLabOrderForThisRequest()">
                                            <button mat-raised-button color="primary" (click)="submitLabOrder()"
                                                [disabled]="!hasSelectedLabTests() || labTestsForm.invalid || !canSubmitLabOrder"
                                                matTooltip="Submit selected lab tests for ordering">
                                                <mat-icon>send</mat-icon>
                                                Submit Lab Order
                                            </button>
                                            <ng-container *ngIf="hasLabOrderForThisRequest()">
                                                <span class="status-message green"><mat-icon>check_circle</mat-icon> Lab
                                                    tests ordered</span>
                                            </ng-container>
                                            <span class="selected-count" *ngIf="getSelectedLabTestsCount() > 0">
                                                {{ getSelectedLabTestsCount() }} test(s) selected
                                            </span>
                                        </div>
                                    </div>
                                    <!-- End of hasSelectedLabOrderPhysician wrapper -->
                                </div>
                                <!-- End of canSubmitLabOrder wrapper -->
                            </div>
                        </div>
                    </mat-expansion-panel>

                </form>

                <form [formGroup]="radiologyForm">
                    <mat-expansion-panel [expanded]="isExpanded('radiology_request')" id="radiology_request"
                        *ngIf="useDynamicLayout ? visibleSections.radiologyRequest : true">
                        <mat-expansion-panel-header>
                            <mat-panel-title>REQUEST FOR RADIOLOGY</mat-panel-title>
                        </mat-expansion-panel-header>

                        <div class="icd-container">
                            <h3>Selected ICD-10 Codes</h3>
                            <app-icd-code-selector [patientSex]="icdCodingSex" [patientAge]="icdCodingAge" [defaultOnsetDate]="icdDefaultOnsetDate" [initialCodes]="selectedMotivationIcdCodes"
                                (codesChanged)="onIcdCodesChanged($event)" [isRequired]="true"></app-icd-code-selector>
                        </div>

                        <mat-form-field appearance="outline" class="full-width m-t-10">
                            <mat-label>Request for Radiology</mat-label>
                            <textarea matInput appFindReplace cdkTextareaAutosize="false" rows="20" formControlName="radiologyRequest"
                                #radiologyRequestTextarea></textarea>
                        </mat-form-field>

                        <mat-form-field appearance="outline" *ngIf="radiologyForm.get('radiologyRequest')?.value"
                            class="full-width">
                            <textarea matInput placeholder="Instruct MedicAI" cdkTextareaAutosize="true" rows="1"
                                #radiologyRequestAutosize="cdkTextareaAutosize" formControlName="radiologyRequestPrompt"
                                (keydown.enter)="aiPerformUserPrompt('radiologyRequest', $event)"></textarea>
                            <button matSuffix mat-icon-button (click)="aiPerformUserPrompt('radiologyRequest')">
                                <mat-icon>send</mat-icon>
                            </button>
                            <mat-hint>
                                Customize your output
                                <mat-icon
                                    matTooltip="You can say things like, output as paragraphs, or add additional information to X heading.">info</mat-icon>
                            </mat-hint>
                        </mat-form-field>

                        <div class="actions wrap">
                            <ng-container *ngIf="radiologyForm.get('radiologyRequest')?.value">
                                <ng-container *ngIf="canSign; else radiologyCantSign">
                                    <button mat-raised-button color="secondary"
                                        [matMenuTriggerFor]="radiologyDownloadMenu">
                                        <mat-icon>file_download</mat-icon> Download
                                    </button>
                                    <mat-menu #radiologyDownloadMenu="matMenu" class="p-10">
                                        <mat-checkbox formControlName="includeSignatureOnPdf" class="checkbox"
                                            (click)="$event.stopPropagation()">
                                            {{isPhysician ? 'Attach Signature' : 'Attach Doctor\'s Signature'}}
                                        </mat-checkbox>
                                        <mat-form-field *ngIf="hasSignaturePermission" appearance="outline"
                                            class="full-width" (keydown)="handleMenuKeydown($event)"
                                            (click)="$event.stopPropagation()">
                                            <mat-label>Select Doctor's Signature</mat-label>
                                            <mat-select formControlName="selectedDoctor">
                                                <mat-option *ngFor="let doctor of groupUsers" [value]="doctor">
                                                    {{doctor.title}} {{ doctor.name }} {{doctor.surname}}
                                                </mat-option>
                                            </mat-select>
                                        </mat-form-field>
                                        <mat-form-field appearance="outline" class="full-width"
                                            (keydown)="handleMenuKeydown($event)" (click)="$event.stopPropagation()">
                                            <mat-label>Document Date</mat-label>
                                            <input matInput [matDatepicker]="pdfDatePicker_radiology" formControlName="pdfDate" placeholder="DD/MM/YYYY">
                                            <mat-datepicker-toggle matSuffix [for]="pdfDatePicker_radiology"></mat-datepicker-toggle>
                                            <mat-datepicker #pdfDatePicker_radiology></mat-datepicker>
                                        </mat-form-field>
                                        <button mat-raised-button color="primary" class="full-width"
                                            (click)="downloadAsPdf('radiologyRequest')">
                                            Download PDF <mat-icon>file_download</mat-icon>
                                        </button>
                                    </mat-menu>
                                </ng-container>
                                <ng-template #radiologyCantSign>
                                    <button mat-raised-button color="primary"
                                        (click)="downloadAsPdf('radiologyRequest')">
                                        <mat-icon>file_download</mat-icon> Download PDF
                                    </button>
                                </ng-template>
                                <ng-container *ngIf="hasTimlineData('radiologyRequest')">
                                    <span class="status-message green"><mat-icon>check_circle</mat-icon> Request exists
                                        in timeline</span>
                                </ng-container>
                                <button mat-raised-button color="primary"
                                    (click)="createPatientNote('radiologyRequest', 'Request for Radiology')"
                                    matTooltip="Create a note in the clinical timeline using this information">
                                    <mat-icon>library_add</mat-icon> Validate To Timeline
                                </button>
                            </ng-container>
                            <button mat-raised-button class="ai-feature" (click)="aiGenerateRadiologyRequest()"
                                matTooltip="Generate a request for radiology using MedicAI"
                                *ngIf="!radiologyForm.get('radiologyRequest')?.value">
                                <mat-icon svgIcon="medic_ai"></mat-icon> Generate
                            </button>
                        </div>

                    </mat-expansion-panel>
                </form>

                <form [formGroup]="tasksForm">

                    <mat-expansion-panel [expanded]="isExpanded('tasks')" id="tasks"
                        *ngIf="useDynamicLayout ? visibleSections.tasks : true">
                        <mat-expansion-panel-header>
                            <mat-panel-title>TASK LIST</mat-panel-title>
                        </mat-expansion-panel-header>


                        <div class="icd-container">
                            <div class="field-row ">
                                <h3>Suggested Tasks</h3>
                                <button mat-raised-button color="primary" (click)="addTask($event, null)"
                                    matTooltip="Add a new task to the patient file">
                                    <mat-icon>add</mat-icon> Add New Task
                                </button>
                            </div>

                            <mat-list class="full-width">
                                <mat-list-item class="auto-height"
                                    *ngFor="let task of tasksForm.get('suggestedAdminTasks')?.value?.tasks">
                                    <div class="icd-item">
                                        <div class="icd-details">
                                            <p matLine class="icd-title">{{task.task}}</p>
                                            <p matLine class="icd-score">Relevance: {{task.score}}%</p>
                                        </div>
                                        <div class="view-icon">
                                            <button mat-icon-button [matTooltip]="task.reason">
                                                <mat-icon>info</mat-icon>
                                            </button>
                                            <button mat-icon-button color="primary" *ngIf="canAddTask(task.task)"
                                                matTooltip="Add this task to the patient file"
                                                (click)="addTask($event, task)">
                                                <mat-icon>library_add</mat-icon>
                                            </button>
                                        </div>
                                    </div>
                                </mat-list-item>
                            </mat-list>

                            <div class="actions wrap" *ngIf="!tasksForm.get('suggestedAdminTasks')?.value?.tasks">
                                <button mat-raised-button class="ai-feature" (click)="aiGenerateTaskList()"
                                    matTooltip="Generate a list of admin task suggestions using MedicAI">
                                    <mat-icon svgIcon="medic_ai"></mat-icon> Generate
                                </button>
                            </div>
                        </div>

                    </mat-expansion-panel>

                </form>

                <!--
                  ══ RISK MANAGEMENT (E2/E5, tasks/risk-management.md) ═══════════════════════

                  Facility failures the doctor mentioned in passing — "there was no 3-0 vicryl",
                  "we waited forty minutes for a porter". Modelled on the TASK LIST panel above.

                  E5: the panel renders only when there is something in it. `suggestedRiskItems`
                  is NULL, not `[]`, on every request predating this feature and every recording
                  where the extractor found nothing, so an absent section is the COMMON case and
                  is silent. Note the `*ngIf` differs from the panels around it — those fall back
                  to `true` when the dynamic layout is off; this one does not, because a
                  governance section with nothing in it is noise on a clinical screen.
                -->
                <form [formGroup]="riskForm">

                    <!-- Gated exactly like TASK LIST above (:874): hidden on a wide screen until the
                         sidebar reveals it, always present on a narrow one. The old extra
                         `hasRiskManagementContent &&` term made this the one section that vanished when
                         it had nothing yet — which is also when you want to add the first item by hand. -->
                    <mat-expansion-panel [expanded]="isExpanded('riskManagement')" id="riskManagement"
                        *ngIf="useDynamicLayout ? visibleSections.riskManagement : true">
                        <mat-expansion-panel-header>
                            <mat-panel-title>RISK MANAGEMENT</mat-panel-title>
                        </mat-expansion-panel-header>

                        <div class="icd-container">

                            <!--
                              E2 — the shared suggestion list. Same component the ward-round note
                              detail uses (F2), so the verbatim quote (D8) and the dedupe guard
                              (E4) exist in exactly one place.
                            -->
                            <app-risk-suggestion-list [suggestions]="riskForm.get('suggestedRiskItems')?.value"
                                [addedIssues]="addedRiskIssues" [disabled]="addingRiskItem"
                                heading="Suggested Facility Issues" [showAddNew]="true"
                                (addSuggestion)="onAddRiskSuggestion($event)"
                                (addNew)="onAddRiskItemManually()">
                            </app-risk-suggestion-list>

                        </div>

                    </mat-expansion-panel>

                </form>

                <mat-expansion-panel [expanded]="isExpanded('consent')" id="consent" class="darkest"
                    *ngIf="useDynamicLayout ? visibleSections.consent : true">
                    <mat-expansion-panel-header>
                        <mat-panel-title>TREATMENT CONSENT</mat-panel-title>
                    </mat-expansion-panel-header>
                    <form [formGroup]="consentForm">
                        <div class="field-row">

                            <mat-form-field appearance="outline" class="full-width">
                                <mat-label>Date</mat-label>
                                <input matInput [matDatepicker]="dateDatePicker" formControlName="date"
                                    placeholder="DD/MM/YYYY" [max]="todayDate">
                                <mat-datepicker-toggle matSuffix [for]="dateDatePicker"></mat-datepicker-toggle>
                                <mat-datepicker #dateDatePicker></mat-datepicker>
                            </mat-form-field>

                            <mat-form-field appearance="outline" class="full-width">
                                <mat-label>Requesting Physician</mat-label>
                                <mat-select formControlName="physicianId">
                                    <mat-option *ngFor="let user of groupUsers" [value]="user.userId">{{user.title}}
                                        {{user.name}}
                                        {{user.surname}}</mat-option>
                                </mat-select>
                            </mat-form-field>

                            <mat-form-field appearance="outline" class="full-width">
                                <mat-label>Consent Type</mat-label>
                                <mat-select formControlName="consentType" (selectionChange)="selectConsentType($event)">
                                    <mat-option *ngFor="let consentType of consentTypes" [value]="consentType">
                                        {{ consentType.label }} {{consentType.consentType ==='interactive' ? '
                                        (Interactive AI)':'' }}
                                    </mat-option>
                                </mat-select>
                            </mat-form-field>
                        </div>
                    </form>
                    <div class="field-row">
                        <ng-container *ngIf="selectedConsentForm?.text">

                            <ng-container *ngIf="selectedConsentForm.consentType === 'interactive'">

                                <mat-form-field appearance="outline" class="full-width">
                                    <mat-label>Procedure Title</mat-label>
                                    <input matInput [(ngModel)]="selectedConsentForm.procedureTitle"
                                        placeholder="Procedure Title" type="text">
                                </mat-form-field>

                                <mat-form-field appearance="outline" class="full-width">
                                    <mat-label>Condition</mat-label>
                                    <input matInput [(ngModel)]="selectedConsentForm.conditionText"
                                        placeholder="Condition" type="text">
                                </mat-form-field>

                                <mat-form-field appearance="outline" class="full-width">
                                    <mat-label>Highlighted Risks</mat-label>
                                    <textarea matInput [(ngModel)]="selectedConsentForm.specificRisksText"
                                        rows="4"></textarea>
                                </mat-form-field>

                                <h3><strong>CONSENT FOR {{selectedConsentForm.procedureTitle}}</strong></h3>
                                <div [innerText]="selectedConsentForm.condition + selectedConsentForm.conditionText"
                                    class="preserve-line-breaks full-width"></div><br>
                                <div [innerText]="selectedConsentForm.treatment + selectedConsentForm.procedureTitle"
                                    class="preserve-line-breaks full-width"></div><br>
                                <div [innerText]="selectedConsentForm.generalRisks"
                                    class="preserve-line-breaks full-width">
                                </div>
                                <br>
                                <div [innerText]="selectedConsentForm.specificRisks + selectedConsentForm.specificRisksText"
                                    class="preserve-line-breaks full-width"></div><br>
                                <div [innerText]="selectedConsentForm.text" class="preserve-line-breaks full-width">
                                </div>
                                <br>
                            </ng-container>

                            <ng-container *ngIf="selectedConsentForm.consentType === 'standard'">
                                <div [innerText]="selectedConsentForm.text" class="preserve-line-breaks full-width">
                                </div>
                            </ng-container>

                            <ng-container
                                *ngIf="selectedConsentForm.confirmSide && selectedConsentForm.sideOptions?.length">
                                <mat-form-field appearance="outline" class="m-t-20 full-width">
                                    <mat-label>Procedure Side</mat-label>
                                    <mat-select [(ngModel)]="selectedConsentForm.selectedProcedureSide" required>
                                        <mat-option *ngFor="let option of selectedConsentForm.sideOptions"
                                            [value]="option">
                                            {{ option }}
                                        </mat-option>
                                    </mat-select>
                                </mat-form-field>
                            </ng-container>


                            <ng-container
                                *ngIf="selectedConsentForm.confirmImplant && selectedConsentForm.implantOptions.length">
                                <mat-form-field appearance="outline" class="m-t-20 full-width">
                                    <mat-label>Implant</mat-label>
                                    <mat-select [(ngModel)]="selectedConsentForm.selectedImplant" required>
                                        <mat-option *ngFor="let option of selectedConsentForm.implantOptions"
                                            [value]="option">
                                            {{ option }}
                                        </mat-option>
                                    </mat-select>
                                </mat-form-field>
                            </ng-container>


                            <ng-container
                                *ngIf="selectedConsentForm.confirmTransfusion && selectedConsentForm.transfusionOptions.length">
                                <mat-form-field appearance="outline" class="m-t-20 full-width">
                                    <mat-label>Blood Transfusion</mat-label>
                                    <mat-select [(ngModel)]="selectedConsentForm.selectedTransfusion" required>
                                        <mat-option *ngFor="let option of selectedConsentForm.transfusionOptions"
                                            [value]="option">
                                            {{ option }}
                                        </mat-option>
                                    </mat-select>
                                </mat-form-field>
                            </ng-container>


                            <ng-container *ngIf="selectedConsentForm.additionalRequiredConsent?.length">
                                <div class="field-column">
                                    <h3 class="p-t-10">REQUIRED CONSENT</h3>
                                    <p><strong>I Consent to the following:</strong></p>
                                    <mat-radio-group [(ngModel)]="selectedConsentForm.selectedRequiredConsent">
                                        <mat-radio-button
                                            *ngFor="let consent of selectedConsentForm.additionalRequiredConsent"
                                            [value]="consent">
                                            {{ consent }}
                                        </mat-radio-button>
                                    </mat-radio-group>
                                </div>
                            </ng-container>


                            <ng-container *ngIf="selectedConsentForm.conditionalConsent?.length">
                                <div class="field-column">
                                    <h3 class="p-t-10">OPTIONAL CONSENT</h3>
                                    <p><strong>I Consent to the following:</strong></p>
                                    <mat-checkbox *ngFor="let consent of selectedConsentForm.conditionalConsent"
                                        [(ngModel)]="consent.consentGiven">{{
                                        consent.name }}</mat-checkbox>
                                </div>
                            </ng-container>

                            <ng-container *ngIf="selectedConsentForm.informationSheets?.length">
                                <div class="field-column">
                                    <h3 class="p-t-10 m-b-0">INFORMATION SHEETS</h3>
                                    <mat-checkbox *ngFor="let sheet of selectedConsentForm.informationSheets"
                                        [(ngModel)]="sheet.sendSheet"><a [href]="sheet.mediaUrl" target="_blank"
                                            matTooltip="Download information sheet">{{sheet.name }}</a>
                                    </mat-checkbox>
                                </div>
                            </ng-container>

                            <!--
                                §29 - THE PRACTICE'S DOCUMENTS, ON THE OTHER SCREEN THAT SENDS A
                                CONSENT REQUEST.

                                `patient-consent-detail` (the manual "Add Patient Consent" screen)
                                got this picker; this section did not, and the two post the SAME
                                body to the SAME endpoint. A clinician who built the consent from
                                an AI request therefore had to raise a second, separate send for
                                the paperwork that belongs with it.

                                It sits BESIDE the information sheets above rather than replacing
                                them, exactly as it does there: §14.6 decision 2 retires those into
                                this picker later, and the two coexist until it does.

                                It hides itself when the practice has no documents, so a practice
                                that has never uploaded one sees nothing new here.
                            -->
                            <div class="field-column">
                                <app-document-attachment-picker context="consentRequest"
                                    [patientId]="patient?.id" [groupId]="patient?.groupId"
                                    [selection]="selectedConsentDocumentIds"
                                    label="Documents to send with this request"
                                    (selectionChange)="selectedConsentDocumentIds = $event"
                                    data-test="ai-request-consent-document-picker">
                                </app-document-attachment-picker>
                            </div>
                        </ng-container>
                    </div>

                    <div class="actions p-t-10 wrap">
                        <ng-container *ngIf="consentForm.get('consentType')?.value && !stateUnit">
                            <mat-form-field appearance="outline">
                                <mat-label>Email</mat-label>
                                <input matInput type="email" [(ngModel)]="selectedConsentForm.email">
                            </mat-form-field>
                            <button mat-raised-button color="primary" (click)="sendConsent()"
                                matTooltip="Send this to the patient via email">
                                <mat-icon>send</mat-icon> Send To Patient
                            </button>
                        </ng-container>
                        <ng-container *ngIf="hasConsentForThisRequest()">
                            <span class="status-message green"><mat-icon>check_circle</mat-icon> Consent sent</span>
                        </ng-container>

                    </div>

                </mat-expansion-panel>

                <form [formGroup]="lomForm">
                    <mat-expansion-panel [expanded]="isExpanded('lom')" id="lom"
                        *ngIf="useDynamicLayout ? visibleSections.lom : true">
                        <mat-expansion-panel-header>
                            <mat-panel-title>LETTER OF MOTIVATION</mat-panel-title>
                        </mat-expansion-panel-header>

                        <div class="field-row">
                            <mat-form-field appearance="outline">
                                <mat-label>Medical Aid/Insurance Name</mat-label>
                                <input matInput formControlName="medicalAid">
                            </mat-form-field>

                            <mat-form-field appearance="outline">
                                <mat-label>Medical Aid/Insurance Number</mat-label>
                                <input matInput formControlName="medicalAidNumber">
                            </mat-form-field>

                            <mat-form-field appearance="outline">
                                <mat-label>Medical Aid/Insurance Plan</mat-label>
                                <input matInput formControlName="medicalAidPlan">
                            </mat-form-field>
                        </div>

                        <div class="icd-container">
                            <h3>Selected ICD-10 Codes</h3>
                            <app-icd-code-selector [patientSex]="icdCodingSex" [patientAge]="icdCodingAge" [defaultOnsetDate]="icdDefaultOnsetDate" [initialCodes]="selectedMotivationIcdCodes"
                                (codesChanged)="onIcdCodesChanged($event)" [isRequired]="true"></app-icd-code-selector>
                        </div>

                        <div class="toggle-container" *ngIf="!stateUnit">
                            <label class="mat-mdc-slide-toggle-content left" for="mat-mdc-slide-toggle-1-button">
                                Other Motivation</label>
                            <mat-slide-toggle formControlName="surgicalMotivationLetter" name="surgicalMotivationLetter"
                                class="toggle">
                            </mat-slide-toggle>
                            <label class="mat-mdc-slide-toggle-content right"
                                for="mat-mdc-slide-toggle-1-button">Surgical Motivation</label>
                        </div>

                        <ng-container *ngIf="lomForm.get('surgicalMotivationLetter')?.value && !stateUnit">
                            <mat-form-field appearance="outline" class="m-t-20 full-width">
                                <mat-label>Detected Procedure Coding Bundle</mat-label>
                                <mat-select formControlName="motivationCodingBundleId"
                                    (selectionChange)="selectMotivationCodingBundle($event)">
                                    <mat-option *ngFor="let bundle of codingBundles" [value]="bundle.id">
                                        {{ bundle.label }}
                                    </mat-option>
                                </mat-select>
                            </mat-form-field>

                            <div class="icd-container">
                                <h3>Selected Procedure Codes</h3>
                                <app-procedure-code-selector #motivationProcedureCodeSelector
                                    [initialCodes]="selectedMotivationProcedureCodes"
                                    (codesChanged)="onSelectedMotivationProcedureCodesChanged($event, null)"
                                    [providedStartTime]="notesForm.get('startTime')?.value"
                                    [providedFinishTime]="notesForm.get('finishTime')?.value"
                                    [patient]="patient"></app-procedure-code-selector>
                            </div>


                            <div class="icd-container m-t-10">
                                <ng-container *ngIf="lomForm.get('suggestedMotivationProcedureCodes')?.value?.codes">
                                    <h4>Suggested Procedure Codes</h4>
                                    <mat-list class="full-width">
                                        <mat-list-item class="auto-height"
                                            *ngFor="let code of lomForm.get('suggestedMotivationProcedureCodes')?.value?.codes">
                                            <div class="icd-item">
                                                <div class="icd-details">
                                                    <p matLine class="icd-title">{{code.code}} - {{code.description}}
                                                    </p>
                                                    <p matLine class="icd-score">Relevance: {{code.score}}%</p>
                                                </div>
                                                <div class="view-icon">
                                                    <button mat-icon-button [matTooltip]="code.reason">
                                                        <mat-icon>info</mat-icon>
                                                    </button>
                                                    <button mat-icon-button color="primary"
                                                        matTooltip="Add this code to the motivation letter"
                                                        (click)="addMotivationProcedureCode($event, code)">
                                                        <mat-icon>library_add</mat-icon>
                                                    </button>
                                                </div>
                                            </div>
                                        </mat-list-item>
                                    </mat-list>
                                </ng-container>
                            </div>
                        </ng-container>
                        <ng-container *ngIf="!lomForm.get('surgicalMotivationLetter')?.value">
                            <mat-form-field appearance="outline" class="full-width m-b-10">
                                <mat-label>Reason For Motivation</mat-label>
                                <input matInput formControlName="motivationReason" type="text">
                                <mat-hint class="hint">You can use this to generate a motivation for medication, other
                                    treatments, etc.</mat-hint>
                            </mat-form-field>
                        </ng-container>


                        <mat-form-field appearance="outline" class="full-width m-t-10">
                            <mat-label>Letter Of Motivation</mat-label>
                            <textarea matInput appFindReplace cdkTextareaAutosize="false" rows="20"
                                formControlName="letterOfMotivation" #letterOfMotivationTextarea></textarea>
                        </mat-form-field>

                        <mat-form-field appearance="outline" *ngIf="lomForm.get('letterOfMotivation')?.value"
                            class="full-width">
                            <textarea matInput placeholder="Instruct MedicAI" cdkTextareaAutosize="true" rows="1"
                                #letterOfMotivationAutosize="cdkTextareaAutosize"
                                formControlName="letterOfMotivationPrompt"
                                (keydown.enter)="aiPerformUserPrompt('letterOfMotivation', $event)"></textarea>
                            <button matSuffix mat-icon-button (click)="aiPerformUserPrompt('letterOfMotivation')">
                                <mat-icon>send</mat-icon>
                            </button>
                            <mat-hint>
                                Customize your output
                                <mat-icon
                                    matTooltip="You can say things like, output as paragraphs, or add additional information to X heading.">info</mat-icon>
                            </mat-hint>
                        </mat-form-field>

                        <div class="actions wrap">
                            <ng-container *ngIf="lomForm.get('letterOfMotivation')?.value">
                                <ng-container *ngIf="canSign; else cantSign">
                                    <button mat-raised-button color="secondary" [matMenuTriggerFor]="downloadMenu">
                                        <mat-icon>file_download</mat-icon> Download
                                    </button>
                                    <mat-menu #downloadMenu="matMenu" class="p-10">
                                        <mat-checkbox formControlName="includeSignatureOnPdf" class="checkbox"
                                            (click)="$event.stopPropagation()">
                                            {{isPhysician ?'Attach Signature' : 'Attach Doctor\'s Signature'}}
                                        </mat-checkbox>
                                        <mat-form-field *ngIf="hasSignaturePermission" appearance="outline"
                                            class="full-width" (keydown)="handleMenuKeydown($event)"
                                            (click)="$event.stopPropagation()">
                                            <mat-label>Select Doctor's Signature</mat-label>
                                            <mat-select formControlName="selectedDoctor">
                                                <mat-option *ngFor="let doctor of groupUsers" [value]="doctor">
                                                    {{doctor.title}} {{ doctor.name }} {{doctor.surname}}
                                                </mat-option>
                                            </mat-select>
                                        </mat-form-field>

                                        <mat-form-field appearance="outline" class="full-width"
                                            (keydown)="handleMenuKeydown($event)" (click)="$event.stopPropagation()">
                                            <mat-label>Document Date</mat-label>
                                            <input matInput [matDatepicker]="pdfDatePicker_lom" formControlName="pdfDate" placeholder="DD/MM/YYYY">
                                            <mat-datepicker-toggle matSuffix [for]="pdfDatePicker_lom"></mat-datepicker-toggle>
                                            <mat-datepicker #pdfDatePicker_lom></mat-datepicker>
                                        </mat-form-field>
                                        <button mat-raised-button color="primary" class="full-width"
                                            (click)="downloadAsPdf('letterOfMotivation')">
                                            Download PDF <mat-icon>file_download</mat-icon>
                                        </button>
                                    </mat-menu>
                                </ng-container>
                                <ng-template #cantSign>
                                    <button mat-raised-button color="primary"
                                        (click)="downloadAsPdf('letterOfMotivation')">
                                        <mat-icon>file_download</mat-icon> Download PDF
                                    </button>
                                </ng-template>
                                <ng-container *ngIf="hasTimlineData('letterOfMotivation')">
                                    <span class="status-message green"><mat-icon>check_circle</mat-icon> Letter exists
                                        in
                                        timeline</span>
                                </ng-container>
                                <button mat-raised-button color="primary"
                                    (click)="createPatientNote('letterOfMotivation', 'Letter Of Motivation')"
                                    matTooltip="Create a note in the clinical timeline using this information">
                                    <mat-icon>library_add</mat-icon> Validate To Timeline
                                </button>
                            </ng-container>
                            <button mat-raised-button class="ai-feature" (click)="aiGenerateLetterOfMotivation()"
                                matTooltip="Generate a letter of motivation using MedicAI"
                                *ngIf="!lomForm.get('letterOfMotivation')?.value">
                                <mat-icon svgIcon="medic_ai"></mat-icon> Generate
                            </button>
                        </div>

                    </mat-expansion-panel>
                </form>

                <form [formGroup]="colleagueForm">

                    <mat-expansion-panel [expanded]="isExpanded('letter_to_colleague')" id="letter_to_colleague"
                        class="darkest" *ngIf="useDynamicLayout ? visibleSections.letterToColleague : true">
                        <mat-expansion-panel-header>
                            <mat-panel-title>LETTER TO COLLEAGUE</mat-panel-title>
                        </mat-expansion-panel-header>

                        <div class="field-row">
                            <mat-form-field appearance="outline">
                                <mat-label>Colleague</mat-label>
                                <input matInput formControlName="colleagueName" [matAutocomplete]="auto" />
                                <mat-autocomplete #auto="matAutocomplete"
                                    (optionSelected)="selectColleague($event.option.value)">
                                    <mat-option *ngFor="let colleague of filteredColleagues$ | async"
                                        [value]="colleague">
                                        {{ colleague.title }} {{colleague.name}} {{colleague.surname}}
                                    </mat-option>
                                </mat-autocomplete>
                            </mat-form-field>

                            <mat-form-field appearance="outline">
                                <mat-label>Speciality</mat-label>
                                <mat-select formControlName="colleagueSpeciality">
                                    <mat-option *ngFor="let speciality of specialities" [value]="speciality.value">
                                        {{ speciality.text }}
                                    </mat-option>
                                </mat-select>
                            </mat-form-field>

                            <mat-form-field appearance="outline">
                                <mat-label>Email Address</mat-label>
                                <input matInput formControlName="colleagueEmail" type="email">
                            </mat-form-field>
                        </div>


                        <mat-form-field appearance="outline" class="full-width m-b-10">
                            <mat-label>Reason For Letter</mat-label>
                            <input matInput formControlName="letterReason" type="text">
                            <mat-hint class="hint">You can use this to generate a referral letter, update on patient
                                status, etc.</mat-hint>
                        </mat-form-field>

                        <mat-form-field appearance="outline" class="full-width m-t-10">
                            <mat-label>Letter to Colleage</mat-label>
                            <textarea matInput appFindReplace cdkTextareaAutosize="false" rows="20" formControlName="letterToColleague"
                                #letterToColleagueTextarea></textarea>
                        </mat-form-field>

                        <mat-form-field appearance="outline" *ngIf="colleagueForm.get('letterToColleague')?.value"
                            class="full-width">
                            <textarea matInput placeholder="Instruct MedicAI" cdkTextareaAutosize="true" rows="1"
                                #letterToColleagueAutosize="cdkTextareaAutosize"
                                formControlName="letterToColleaguePrompt"
                                (keydown.enter)="aiPerformUserPrompt('letterToColleague', $event)"></textarea>
                            <button matSuffix mat-icon-button (click)="aiPerformUserPrompt('letterToColleague')">
                                <mat-icon>send</mat-icon>
                            </button>
                            <mat-hint>
                                Customize your output
                                <mat-icon
                                    matTooltip="You can say things like, output as paragraphs, or add additional information to X heading.">info</mat-icon>
                            </mat-hint>
                        </mat-form-field>


                        <div class="actions wrap">
                            <ng-container *ngIf="colleagueForm.get('letterToColleague')?.value">

                                <ng-container *ngIf="canSign; else cantSign">
                                    <button mat-raised-button color="secondary" [matMenuTriggerFor]="downloadMenu">
                                        <mat-icon>file_download</mat-icon> Download
                                    </button>
                                    <mat-menu #downloadMenu="matMenu" class="p-10">
                                        <mat-checkbox formControlName="includeSignatureOnPdf" class="checkbox"
                                            (click)="$event.stopPropagation()">
                                            {{isPhysician ? 'Attach Signature' : 'Attach Doctor\'s Signature'}}
                                        </mat-checkbox>
                                        <mat-form-field *ngIf="hasSignaturePermission" appearance="outline"
                                            class="full-width" (keydown)="handleMenuKeydown($event)"
                                            (click)="$event.stopPropagation()">
                                            <mat-label>Select Doctor's Signature</mat-label>
                                            <mat-select formControlName="selectedDoctor">
                                                <mat-option *ngFor="let doctor of groupUsers" [value]="doctor">
                                                    {{doctor.title}} {{ doctor.name }} {{doctor.surname}}
                                                </mat-option>
                                            </mat-select>
                                        </mat-form-field>
                                        <mat-form-field appearance="outline" class="full-width"
                                            (keydown)="handleMenuKeydown($event)" (click)="$event.stopPropagation()">
                                            <mat-label>Document Date</mat-label>
                                            <input matInput [matDatepicker]="pdfDatePicker_colleague" formControlName="pdfDate" placeholder="DD/MM/YYYY">
                                            <mat-datepicker-toggle matSuffix [for]="pdfDatePicker_colleague"></mat-datepicker-toggle>
                                            <mat-datepicker #pdfDatePicker_colleague></mat-datepicker>
                                        </mat-form-field>
                                        <button mat-raised-button color="primary" class="full-width"
                                            (click)="downloadAsPdf('letterToColleague')">
                                            Download PDF <mat-icon>file_download</mat-icon>
                                        </button>
                                    </mat-menu>
                                </ng-container>
                                <ng-template #cantSign>
                                    <button mat-raised-button color="primary"
                                        (click)="downloadAsPdf('letterToColleague')">
                                        <mat-icon>file_download</mat-icon> Download PDF
                                    </button>
                                </ng-template>

                                <button mat-icon-button color="primary"
                                    *ngIf="colleagueForm.get('letterToColleague')?.value" appWhatsappDialogTrigger
                                    [patientId]="patient?.id" [patient]="patient"
                                    [initialMessage]="getColleagueLetterPlainText()"
                                    matTooltip="Send this to your colleague via WhatsApp">
                                    <mat-icon svgIcon="whatsapp"></mat-icon>
                                </button>
                                <button mat-raised-button color="primary" [matMenuTriggerFor]="emailMenu" *ngIf="!stateUnit"
                                    [disabled]="!(colleagueForm.get('colleagueName')?.value && colleagueForm.get('colleagueEmail')?.value)"
                                    matTooltip="Send this to your colleague via email">
                                    <mat-icon>send</mat-icon> Send To Colleague
                                </button>

                                <mat-menu #emailMenu="matMenu" class="p-10">
                                    <div mat-menu-content (click)="$event.stopPropagation()">
                                        <mat-checkbox *ngIf="canSign" formControlName="includeSignatureOnEmail"
                                            class="checkbox" (click)="$event.stopPropagation()">
                                            {{isPhysician ?'Attach Signature' : 'Attach Doctor\'s Signature'}}
                                        </mat-checkbox>
                                        <mat-form-field *ngIf="hasSignaturePermission" appearance="outline"
                                            class="full-width" (keydown)="handleMenuKeydown($event)"
                                            (click)="$event.stopPropagation()">
                                            <mat-label>Select Doctor's Signature</mat-label>
                                            <mat-select formControlName="selectedDoctor">
                                                <mat-option *ngFor="let doctor of groupUsers" [value]="doctor">
                                                    {{doctor.title}} {{ doctor.name }} {{doctor.surname}}
                                                </mat-option>
                                            </mat-select>
                                        </mat-form-field>
                                        <mat-form-field appearance="outline" class="full-width"
                                            (keydown)="handleMenuKeydown($event)" (click)="$event.stopPropagation()">
                                            <mat-label>Document Date</mat-label>
                                            <input matInput [matDatepicker]="pdfDatePickerEmail_colleague" formControlName="pdfDate" placeholder="DD/MM/YYYY">
                                            <mat-datepicker-toggle matSuffix [for]="pdfDatePickerEmail_colleague"></mat-datepicker-toggle>
                                            <mat-datepicker #pdfDatePickerEmail_colleague></mat-datepicker>
                                        </mat-form-field>
                                        <button mat-raised-button color="primary" class="full-width"
                                            (click)="sendViaEmail('letterToColleague')"
                                            matTooltip="Send this to your colleague via email">
                                            <mat-icon>send</mat-icon> Send Email
                                        </button>
                                    </div>
                                </mat-menu>
                            </ng-container>
                            <ng-container>
                                <button mat-raised-button class="ai-feature" (click)="aiGenerateLetterToColleague()"
                                    matTooltip="Generate a letter to colleague using MedicAI">
                                    <mat-icon svgIcon="medic_ai"></mat-icon> {{
                                    colleagueForm.get('letterToColleague')?.value ? 'Regenerate' : 'Generate'}}
                                </button>
                            </ng-container>
                        </div>
                    </mat-expansion-panel>

                </form>


                <form [formGroup]="gpForm">
                    <mat-expansion-panel [expanded]="isExpanded('letter_to_gp')" id="letter_to_gp"
                        *ngIf="useDynamicLayout ? visibleSections.letterToGP : true">
                        <mat-expansion-panel-header>
                            <mat-panel-title>LETTER TO {{selectedGroup?.groupSpeciality === 'General Practice' ?
                                'SPECIALIST'
                                :
                                'GP'}}</mat-panel-title>
                        </mat-expansion-panel-header>

                        <div class="field-row">
                            <mat-form-field appearance="outline">
                                <mat-label>{{selectedGroup?.groupSpeciality === 'General Practice' ? 'Specialist' :
                                    'GP'}} Name</mat-label>
                                <input matInput formControlName="generalPractitioner" [matAutocomplete]="teamAuto" />
                                <mat-autocomplete #teamAuto="matAutocomplete"
                                    (optionSelected)="selectTeamMember($event.option.value)">
                                    <mat-option *ngFor="let member of filteredMedicalTeam$ | async" [value]="member">
                                        {{ member.title }} {{member.name}} {{member.surname}}
                                    </mat-option>
                                </mat-autocomplete>
                            </mat-form-field>

                            <mat-form-field appearance="outline">
                                <mat-label>{{selectedGroup?.groupSpeciality === 'General Practice' ? 'Specialist' :
                                    'GP'}} Email Address</mat-label>
                                <input matInput formControlName="generalPractitionerEmail" type="email">
                            </mat-form-field>

                            <app-intl-phone-input [preferredCountries]="['za']"
                                [providedValue]="gpForm.get('generalPractitionerPhone')?.value" [placeholder]="selectedGroup?.groupSpeciality === 'General Practice' ? 'Specialist Phone' :
                                'GP Phone'" (valueChange)="gpForm.patchValue({generalPractitionerPhone:$event})"
                                name="generalPractitionerPhone"></app-intl-phone-input>
                        </div>

                        <mat-form-field appearance="outline" class="full-width">
                            <mat-label>Letter to {{selectedGroup?.groupSpeciality === 'General Practice' ? 'Specialist'
                                :
                                'GP'}}</mat-label>
                            <textarea matInput appFindReplace cdkTextareaAutosize="false" rows="20" formControlName="letterToGP"
                                #letterToGPTextarea></textarea>
                        </mat-form-field>

                        <mat-form-field appearance="outline" *ngIf="gpForm.get('letterToGP')?.value" class="full-width">
                            <textarea matInput placeholder="Instruct MedicAI" cdkTextareaAutosize="true" rows="1"
                                #letterToGPAutosize="cdkTextareaAutosize" formControlName="letterToGPPrompt"
                                (keydown.enter)="aiPerformUserPrompt('letterToGP', $event)"></textarea>
                            <button matSuffix mat-icon-button (click)="aiPerformUserPrompt('letterToGP')">
                                <mat-icon>send</mat-icon>
                            </button>
                            <mat-hint>
                                Customize your output
                                <mat-icon
                                    matTooltip="You can say things like, output as paragraphs, or add additional information to X heading.">info</mat-icon>
                            </mat-hint>
                        </mat-form-field>

                        <div class="actions wrap">
                            <ng-container *ngIf="gpForm.get('letterToGP')?.value">

                                <ng-container *ngIf="canSign; else cantSign">
                                    <button mat-raised-button color="secondary" [matMenuTriggerFor]="downloadMenu">
                                        <mat-icon>file_download</mat-icon> Download
                                    </button>
                                    <mat-menu #downloadMenu="matMenu" class="p-10">
                                        <mat-checkbox formControlName="includeSignatureOnPdf" class="checkbox"
                                            (click)="$event.stopPropagation()">
                                            {{isPhysician ? 'Attach Signature' : 'Attach Doctor\'s Signature'}}
                                        </mat-checkbox>
                                        <mat-form-field *ngIf="hasSignaturePermission" appearance="outline"
                                            class="full-width" (keydown)="handleMenuKeydown($event)"
                                            (click)="$event.stopPropagation()">
                                            <mat-label>Select Doctor's Signature</mat-label>
                                            <mat-select formControlName="selectedDoctor">
                                                <mat-option *ngFor="let doctor of groupUsers" [value]="doctor">
                                                    {{doctor.title}} {{ doctor.name }} {{doctor.surname}}
                                                </mat-option>
                                            </mat-select>
                                        </mat-form-field>
                                        <mat-form-field appearance="outline" class="full-width"
                                            (keydown)="handleMenuKeydown($event)" (click)="$event.stopPropagation()">
                                            <mat-label>Document Date</mat-label>
                                            <input matInput [matDatepicker]="pdfDatePicker_gp" formControlName="pdfDate" placeholder="DD/MM/YYYY">
                                            <mat-datepicker-toggle matSuffix [for]="pdfDatePicker_gp"></mat-datepicker-toggle>
                                            <mat-datepicker #pdfDatePicker_gp></mat-datepicker>
                                        </mat-form-field>
                                        <button mat-raised-button color="primary" class="full-width"
                                            (click)="downloadAsPdf('letterToGP')">
                                            Download PDF <mat-icon>file_download</mat-icon>
                                        </button>
                                    </mat-menu>
                                </ng-container>
                                <ng-template #cantSign>
                                    <button mat-raised-button color="primary" (click)="downloadAsPdf('letterToGP')">
                                        <mat-icon>file_download</mat-icon> Download PDF
                                    </button>
                                </ng-template>

                                <button mat-icon-button color="primary" *ngIf="gpForm.get('letterToGP')?.value"
                                    appWhatsappDialogTrigger [patientId]="patient?.id" [patient]="patient"
                                    [initialMessage]="getGPLetterPlainText()" [matTooltip]="selectedGroup?.groupSpeciality === 'General Practice' ? 'Send this to the patient\'s Specialist via WhatsApp' :
                                    'Send this to the patient\'s GP via WhatsApp'">
                                    <mat-icon svgIcon="whatsapp"></mat-icon>
                                </button>
                                <button mat-raised-button color="primary" [matMenuTriggerFor]="emailMenu" *ngIf="!stateUnit" [matTooltip]="selectedGroup?.groupSpeciality === 'General Practice' ? 'Send this to the patient\'s Specialist via email' :
                                'Send this to the patient\'s GP via email'">
                                    <mat-icon>send</mat-icon> Send To
                                    {{selectedGroup?.groupSpeciality === 'General Practice'
                                    ? 'Specialist' : 'GP'}}
                                </button>
                                <mat-menu #emailMenu="matMenu" class="p-10">
                                    <div mat-menu-content (click)="$event.stopPropagation()">
                                        <mat-checkbox *ngIf="canSign" formControlName="includeSignatureOnEmail"
                                            class="checkbox" (click)="$event.stopPropagation()">
                                            {{isPhysician ?'Attach Signature' : 'Attach Doctor\'s Signature'}}
                                        </mat-checkbox>
                                        <mat-form-field *ngIf="hasSignaturePermission" appearance="outline"
                                            class="full-width" (keydown)="handleMenuKeydown($event)"
                                            (click)="$event.stopPropagation()">
                                            <mat-label>Select Doctor's Signature</mat-label>
                                            <mat-select formControlName="selectedDoctor">
                                                <mat-option *ngFor="let doctor of groupUsers" [value]="doctor">
                                                    {{doctor.title}} {{ doctor.name }} {{doctor.surname}}
                                                </mat-option>
                                            </mat-select>
                                        </mat-form-field>
                                        <mat-form-field appearance="outline" class="full-width"
                                            (keydown)="handleMenuKeydown($event)" (click)="$event.stopPropagation()">
                                            <mat-label>Document Date</mat-label>
                                            <input matInput [matDatepicker]="pdfDatePickerEmail_gp" formControlName="pdfDate" placeholder="DD/MM/YYYY">
                                            <mat-datepicker-toggle matSuffix [for]="pdfDatePickerEmail_gp"></mat-datepicker-toggle>
                                            <mat-datepicker #pdfDatePickerEmail_gp></mat-datepicker>
                                        </mat-form-field>
                                        <button mat-raised-button color="primary" class="full-width"
                                            (click)="sendViaEmail('letterToGP')"
                                            matTooltip="Send this to the patient's GP via email">
                                            <mat-icon>send</mat-icon> Send Email
                                        </button>
                                    </div>
                                </mat-menu>
                            </ng-container>
                            <button mat-raised-button class="ai-feature" (click)="aiGenerateLetterToGP()" [matTooltip]="selectedGroup?.groupSpeciality === 'General Practice' ? 'Generate a letter to the patient\'s Specialist using MedicAI' :
                                'Generate a letter to the patient\'s GP using MedicAI'"
                                *ngIf="!gpForm.get('letterToGP')?.value">
                                <mat-icon svgIcon="medic_ai"></mat-icon> Generate
                            </button>

                        </div>

                    </mat-expansion-panel>
                </form>

                <form [formGroup]="patientForm">

                    <mat-expansion-panel [expanded]="isExpanded('letter_to_patient')" id="letter_to_patient"
                        class="darkest" *ngIf="useDynamicLayout ? visibleSections.letterToPatient : true">
                        <mat-expansion-panel-header>
                            <mat-panel-title>LETTER TO PATIENT</mat-panel-title>
                        </mat-expansion-panel-header>


                        <div class="field-row">
                            <mat-form-field appearance="outline">
                                <mat-label>Email Address</mat-label>
                                <input matInput formControlName="patientEmail" type="email">
                            </mat-form-field>
                        </div>

                        <mat-form-field appearance="outline" class="full-width">
                            <mat-label>Letter to Patient</mat-label>
                            <textarea matInput appFindReplace cdkTextareaAutosize="false" rows="20" formControlName="letterToPatient"
                                #letterToPatientTextarea></textarea>
                        </mat-form-field>

                        <mat-form-field appearance="outline" *ngIf="patientForm.get('letterToPatient')?.value"
                            class="full-width">
                            <textarea matInput placeholder="Instruct MedicAI" cdkTextareaAutosize="true" rows="1"
                                #letterToPatientAutosize="cdkTextareaAutosize" formControlName="letterToPatientPrompt"
                                (keydown.enter)="aiPerformUserPrompt('letterToPatient', $event)"></textarea>
                            <button matSuffix mat-icon-button (click)="aiPerformUserPrompt('letterToPatient')">
                                <mat-icon>send</mat-icon>
                            </button>
                            <mat-hint>
                                Customize your output
                                <mat-icon
                                    matTooltip="You can say things like, output as paragraphs, or add additional information to X heading.">info</mat-icon>
                            </mat-hint>
                        </mat-form-field>

                        <div class="actions wrap">
                            <!-- tasks/patient-app.md §6.1: the saved letter, into the patient's own app. -->
                            <button mat-stroked-button type="button" *ngIf="!stateUnit && editingRequest?.id && patient?.id && editingRequest?.letterToPatient"
                                (click)="openPatientAppShare()" data-test="share-patient-app">
                                <mat-icon>phonelink_ring</mat-icon> Send to patient's app
                            </button>
                            <ng-container *ngIf="patientForm.get('letterToPatient')?.value">

                                <ng-container *ngIf="canSign; else cantSign">
                                    <button mat-raised-button color="secondary" [matMenuTriggerFor]="downloadMenu">
                                        <mat-icon>file_download</mat-icon> Download
                                    </button>
                                    <mat-menu #downloadMenu="matMenu" class="p-10">
                                        <mat-checkbox *ngIf="canSign" formControlName="includeSignatureOnPdf"
                                            class="checkbox" (click)="$event.stopPropagation()">
                                            {{isPhysician ? 'Attach Signature' : 'Attach Doctor\'s Signature'}}
                                        </mat-checkbox>
                                        <mat-form-field *ngIf="hasSignaturePermission" appearance="outline"
                                            class="full-width" (keydown)="handleMenuKeydown($event)"
                                            (click)="$event.stopPropagation()">
                                            <mat-label>Select Doctor's Signature</mat-label>
                                            <mat-select formControlName="selectedDoctor">
                                                <mat-option *ngFor="let doctor of groupUsers" [value]="doctor">
                                                    {{doctor.title}} {{ doctor.name }} {{doctor.surname}}
                                                </mat-option>
                                            </mat-select>
                                        </mat-form-field>
                                        <mat-form-field appearance="outline" class="full-width"
                                            (keydown)="handleMenuKeydown($event)" (click)="$event.stopPropagation()">
                                            <mat-label>Document Date</mat-label>
                                            <input matInput [matDatepicker]="pdfDatePicker_patient" formControlName="pdfDate" placeholder="DD/MM/YYYY">
                                            <mat-datepicker-toggle matSuffix [for]="pdfDatePicker_patient"></mat-datepicker-toggle>
                                            <mat-datepicker #pdfDatePicker_patient></mat-datepicker>
                                        </mat-form-field>
                                        <button mat-raised-button color="primary" class="full-width"
                                            (click)="downloadAsPdf('letterToPatient')">
                                            Download PDF <mat-icon>file_download</mat-icon>
                                        </button>
                                    </mat-menu>
                                </ng-container>
                                <ng-template #cantSign>
                                    <button mat-raised-button color="primary"
                                        (click)="downloadAsPdf('letterToPatient')">
                                        <mat-icon>file_download</mat-icon> Download PDF
                                    </button>
                                </ng-template>
                                <button mat-icon-button color="primary"
                                    *ngIf="patientForm.get('letterToPatient')?.value" appWhatsappDialogTrigger
                                    [patientId]="patient?.id" [patient]="patient"
                                    [initialMessage]="getPatientLetterPlainText()"
                                    matTooltip="Send this to the patient via WhatsApp">
                                    <mat-icon svgIcon="whatsapp"></mat-icon>
                                </button>
                                <button mat-raised-button color="primary" [matMenuTriggerFor]="emailMenu" *ngIf="!stateUnit"
                                    matTooltip="Send this to the patient via email">
                                    <mat-icon>send</mat-icon> Send To Patient
                                </button>
                                <mat-menu #emailMenu="matMenu" class="p-10">
                                    <div mat-menu-content (click)="$event.stopPropagation()">
                                        <mat-checkbox *ngIf="canSign" formControlName="includeSignatureOnEmail"
                                            class="checkbox" (click)="$event.stopPropagation()">
                                            {{isPhysician ?'Attach Signature' : 'Attach Doctor\'s Signature'}}
                                        </mat-checkbox>
                                        <mat-form-field *ngIf="hasSignaturePermission" appearance="outline"
                                            class="full-width" (keydown)="handleMenuKeydown($event)"
                                            (click)="$event.stopPropagation()">
                                            <mat-label>Select Doctor's Signature</mat-label>
                                            <mat-select formControlName="selectedDoctor">
                                                <mat-option *ngFor="let doctor of groupUsers" [value]="doctor">
                                                    {{doctor.title}} {{ doctor.name }} {{doctor.surname}}
                                                </mat-option>
                                            </mat-select>
                                        </mat-form-field>
                                        <mat-form-field appearance="outline" class="full-width"
                                            (keydown)="handleMenuKeydown($event)" (click)="$event.stopPropagation()">
                                            <mat-label>Document Date</mat-label>
                                            <input matInput [matDatepicker]="pdfDatePickerEmail_patient" formControlName="pdfDate" placeholder="DD/MM/YYYY">
                                            <mat-datepicker-toggle matSuffix [for]="pdfDatePickerEmail_patient"></mat-datepicker-toggle>
                                            <mat-datepicker #pdfDatePickerEmail_patient></mat-datepicker>
                                        </mat-form-field>
                                        <button mat-raised-button color="primary" class="full-width"
                                            (click)="sendViaEmail('letterToPatient')"
                                            matTooltip="Send this to the patient via email">
                                            <mat-icon>send</mat-icon> Send Email
                                        </button>
                                    </div>
                                </mat-menu>
                            </ng-container>
                            <button mat-raised-button class="ai-feature" (click)="aiGenerateLetterToPatient()"
                                matTooltip="Generate a letter/consult summary to the patient using MedicAI"
                                *ngIf="!patientForm.get('letterToPatient')?.value">
                                <mat-icon svgIcon="medic_ai"></mat-icon> Generate
                            </button>
                        </div>
                    </mat-expansion-panel>
                </form>
                <form [formGroup]="patientWorkForm">
                    <mat-expansion-panel [expanded]="isExpanded('letter_to_patient_employer')"
                        id="letter_to_patient_employer"
                        *ngIf="useDynamicLayout ? visibleSections.letterToPatientWork : true">
                        <mat-expansion-panel-header>
                            <mat-panel-title>SICK LETTER</mat-panel-title>
                        </mat-expansion-panel-header>


                        <div class="field-row">
                            <mat-form-field appearance="outline">
                                <mat-label>Employer/School</mat-label>
                                <input matInput formControlName="patientEmployer" type="text">
                            </mat-form-field>
                        </div>

                        <mat-form-field appearance="outline" class="full-width">
                            <mat-label>Sick Letter</mat-label>
                            <textarea matInput appFindReplace cdkTextareaAutosize="false" rows="20"
                                formControlName="letterToPatientWork" #letterToPatientWorkTextarea></textarea>
                        </mat-form-field>

                        <mat-form-field appearance="outline" *ngIf="patientWorkForm.get('letterToPatientWork')?.value"
                            class="full-width">
                            <textarea matInput placeholder="Instruct MedicAI" cdkTextareaAutosize="true" rows="1"
                                #letterToPatientWorkAutosize="cdkTextareaAutosize"
                                formControlName="letterToPatientWorkPrompt"
                                (keydown.enter)="aiPerformUserPrompt('letterToPatientWork', $event)"></textarea>
                            <button matSuffix mat-icon-button (click)="aiPerformUserPrompt('letterToPatientWork')">
                                <mat-icon>send</mat-icon>
                            </button>
                            <mat-hint>
                                Customize your output
                                <mat-icon
                                    matTooltip="You can say things like, output as paragraphs, or add additional information to X heading.">info</mat-icon>
                            </mat-hint>
                        </mat-form-field>

                        <div class="actions wrap">
                            <ng-container *ngIf="patientWorkForm.get('letterToPatientWork')?.value">

                                <ng-container *ngIf="canSign; else cantSign">
                                    <button mat-raised-button color="secondary" [matMenuTriggerFor]="downloadMenu">
                                        <mat-icon>file_download</mat-icon> Download
                                    </button>
                                    <mat-menu #downloadMenu="matMenu" class="p-10">
                                        <mat-checkbox *ngIf="canSign" formControlName="includeSignatureOnPdf"
                                            class="checkbox" (click)="$event.stopPropagation()">
                                            {{isPhysician ? 'Attach Signature' : 'Attach Doctor\'s Signature'}}
                                        </mat-checkbox>
                                        <mat-form-field *ngIf="hasSignaturePermission" appearance="outline"
                                            class="full-width" (keydown)="handleMenuKeydown($event)"
                                            (click)="$event.stopPropagation()">
                                            <mat-label>Select Doctor's Signature</mat-label>
                                            <mat-select formControlName="selectedDoctor">
                                                <mat-option *ngFor="let doctor of groupUsers" [value]="doctor">
                                                    {{doctor.title}} {{ doctor.name }} {{doctor.surname}}
                                                </mat-option>
                                            </mat-select>
                                        </mat-form-field>
                                        <mat-form-field appearance="outline" class="full-width"
                                            (keydown)="handleMenuKeydown($event)" (click)="$event.stopPropagation()">
                                            <mat-label>Document Date</mat-label>
                                            <input matInput [matDatepicker]="pdfDatePicker_patientWork" formControlName="pdfDate" placeholder="DD/MM/YYYY">
                                            <mat-datepicker-toggle matSuffix [for]="pdfDatePicker_patientWork"></mat-datepicker-toggle>
                                            <mat-datepicker #pdfDatePicker_patientWork></mat-datepicker>
                                        </mat-form-field>
                                        <button mat-raised-button color="primary" class="full-width"
                                            (click)="downloadAsPdf('letterToPatientWork')">
                                            Download PDF <mat-icon>file_download</mat-icon>
                                        </button>
                                    </mat-menu>
                                </ng-container>
                                <ng-template #cantSign>
                                    <button mat-raised-button color="primary"
                                        (click)="downloadAsPdf('letterToPatientWork')">
                                        <mat-icon>file_download</mat-icon> Download PDF
                                    </button>
                                </ng-template>
                                <button mat-raised-button color="primary" [matMenuTriggerFor]="emailMenu" *ngIf="!stateUnit"
                                    matTooltip="Send this to the patient via email">
                                    <mat-icon>send</mat-icon> Send To Patient
                                </button>
                                <mat-menu #emailMenu="matMenu" class="p-10">
                                    <div mat-menu-content (click)="$event.stopPropagation()">
                                        <mat-checkbox *ngIf="canSign" formControlName="includeSignatureOnEmail"
                                            class="checkbox" (click)="$event.stopPropagation()">
                                            {{isPhysician ?'Attach Signature' : 'Attach Doctor\'s Signature'}}
                                        </mat-checkbox>
                                        <mat-form-field *ngIf="hasSignaturePermission" appearance="outline"
                                            class="full-width" (keydown)="handleMenuKeydown($event)"
                                            (click)="$event.stopPropagation()">
                                            <mat-label>Select Doctor's Signature</mat-label>
                                            <mat-select formControlName="selectedDoctor">
                                                <mat-option *ngFor="let doctor of groupUsers" [value]="doctor">
                                                    {{doctor.title}} {{ doctor.name }} {{doctor.surname}}
                                                </mat-option>
                                            </mat-select>
                                        </mat-form-field>
                                        <mat-form-field appearance="outline" class="full-width"
                                            (keydown)="handleMenuKeydown($event)" (click)="$event.stopPropagation()">
                                            <mat-label>Document Date</mat-label>
                                            <input matInput [matDatepicker]="pdfDatePickerEmail_patientWork" formControlName="pdfDate" placeholder="DD/MM/YYYY">
                                            <mat-datepicker-toggle matSuffix [for]="pdfDatePickerEmail_patientWork"></mat-datepicker-toggle>
                                            <mat-datepicker #pdfDatePickerEmail_patientWork></mat-datepicker>
                                        </mat-form-field>
                                        <button mat-raised-button color="primary" class="full-width"
                                            (click)="sendViaEmail('letterToPatientWork')"
                                            matTooltip="Send this to the patient via email">
                                            <mat-icon>send</mat-icon> Send Email
                                        </button>
                                    </div>
                                </mat-menu>
                            </ng-container>
                            <button mat-raised-button class="ai-feature" (click)="aiGenerateLetterToPatientWork()"
                                matTooltip="Generate a letter to the patient's employer using MedicAI"
                                *ngIf="!patientWorkForm.get('letterToPatientWork')?.value">
                                <mat-icon svgIcon="medic_ai"></mat-icon> Generate
                            </button>
                        </div>
                    </mat-expansion-panel>

                </form>

                <!-- <form [formGroup]="reportForm">
                    <mat-expansion-panel [expanded]="isExpanded('report')" id="report">
                        <mat-expansion-panel-header>
                            <mat-panel-title>PATIENT REPORT</mat-panel-title>
                        </mat-expansion-panel-header>

                        <div class="field-row">
                            <mat-form-field appearance="outline" class="full-width">
                                <mat-label>Start Date</mat-label>
                                <input matInput [matDatepicker]="startDatePicker" formControlName="startDate"
                                    placeholder="DD/MM/YYYY" (dateChange)="filterTimeline()" [max]="todayDate">
                                <mat-datepicker-toggle matSuffix [for]="startDatePicker"></mat-datepicker-toggle>
                                <mat-datepicker #startDatePicker></mat-datepicker>
                            </mat-form-field>

                            <mat-form-field appearance="outline" class="full-width">
                                <mat-label>End Date</mat-label>
                                <input matInput [matDatepicker]="endDatePicker" formControlName="endDate"
                                    placeholder="DD/MM/YYYY" (dateChange)="filterTimeline()" [max]="todayDate">
                                <mat-datepicker-toggle matSuffix [for]="endDatePicker"></mat-datepicker-toggle>
                                <mat-datepicker #endDatePicker></mat-datepicker>
                            </mat-form-field>
                        </div>

                        <mat-selection-list #selectedTimelineItems>
                            <mat-list-option class="auto-height" *ngFor="let item of filteredTimelineData"
                                [value]="item">
                                {{item.date | date: 'dd/MM/yyyy'}} - {{item.title ? item.title : 'Admission Record'}}
                                <mat-icon
                                    [matTooltip]="item.type === 'note' ? item.notes : item.dischargeSummary">info</mat-icon>
                            </mat-list-option>
                        </mat-selection-list>

                        <mat-form-field appearance="outline" class="full-width">
                            <mat-label>Report Type</mat-label>
                            <mat-select formControlName="noteTypeId">
                                <mat-option *ngFor="let noteType of noteTypes" [value]="noteType.id">
                                    {{ noteType.label }}
                                </mat-option>
                            </mat-select>
                        </mat-form-field>

                        <mat-form-field appearance="outline" class="full-width m-t-10">
                            <mat-label>Patient Report</mat-label>
                            <textarea matInput appFindReplace cdkTextareaAutosize="false" rows="20" formControlName="patientReport"
                                #patientReportTextarea></textarea>
                        </mat-form-field>

                        <mat-form-field appearance="outline" *ngIf="reportForm.get('patientReport')?.value"
                            class="full-width">
                            <textarea matInput placeholder="Instruct MedicAI" cdkTextareaAutosize="true" rows="1"
                                #patientReportAutosize="cdkTextareaAutosize" formControlName="patientReportPrompt"
                                (keydown.enter)="aiPerformUserPrompt('patientReport', $event)"></textarea>
                            <button matSuffix mat-icon-button (click)="aiPerformUserPrompt('patientReport')">
                                <mat-icon>send</mat-icon>
                            </button>
                            <mat-hint>
                                Customize your output
                                <mat-icon
                                    matTooltip="You can say things like, output as paragraphs, or add additional information to X heading.">info</mat-icon>
                            </mat-hint>
                        </mat-form-field>

                        <div class="actions wrap">
                            <ng-container *ngIf="reportForm.get('patientReport')?.value">
                                <button mat-raised-button color="secondary" (click)="downloadAsPdf('patientReport')">
                                    <mat-icon>file_download</mat-icon> Download
                                </button>
                                <ng-container *ngIf="hasTimlineData('patientReport')">
                                    <span class="status-message green"><mat-icon>check_circle</mat-icon> Report exists
                                        in
                                        timeline</span>
                                </ng-container>
                                <button mat-raised-button color="primary"
                                    (click)="createPatientNote('patientReport', 'Patient Report')"
                                    matTooltip="Create a note in the clinical timeline using this information">
                                    <mat-icon>library_add</mat-icon> Save To Timeline
                                </button>
                            </ng-container>
                            <button mat-raised-button class="ai-feature" (click)="aiGeneratePatientReport()"
                                matTooltip="Generate a patient report using the selected timeline information using MedicAI"
                                [disabled]="selectedTimelineItems.selectedOptions.selected.length === 0">
                                <mat-icon svgIcon="medic_ai"></mat-icon> {{reportForm.get('patientReport')?.value ?
                                'Regenerate':'Generate'}}
                            </button>
                        </div>

                    </mat-expansion-panel>
                </form> -->
            </mat-accordion>
        </ng-container>
        <div class="mobile-spacer"></div>
        <div class="desktop-spacer"></div>
    </div>
    <div class="sidebar-container request-sidebar" [class.dynamic-layout]="useDynamicLayout">
        <p *ngIf="useDynamicLayout" class="sidebar-title">Workflow Tracking</p>
        <mat-nav-list>
            <mat-list-item (click)="scrollTo('top', true)">
                <div class="list-content"><span>Details</span><mat-icon *ngIf="useDynamicLayout">edit</mat-icon>
                </div>
            </mat-list-item>
            <mat-list-item (click)="scrollTo('notes')">
                <div class="list-content"><span>Notes</span><mat-icon
                        *ngIf="useDynamicLayout && (completedSections.notes || hasTimlineData('notes'))"
                        class="green-icon">done</mat-icon>
                </div>
            </mat-list-item>
            <mat-list-item (click)="scrollTo('file_update')">
                <div class="list-content"><span>File
                        Update</span>
                    <mat-icon *ngIf="useDynamicLayout && completedSections.fileUpdate"
                        class="green-icon">done</mat-icon>
                </div>
            </mat-list-item>
            <mat-list-item (click)="scrollTo('medication')"
                [appGroupSpecialtyHide]="{ specialties: ['Physiotherapy'], condition: 'exclude' }">
                <div class="list-content"><span>Medication</span><mat-icon
                        *ngIf="useDynamicLayout && completedSections.medication" class="green-icon">done</mat-icon>
                </div>
            </mat-list-item>
            <mat-list-item (click)="scrollTo('labTests')"
                [appGroupSpecialtyHide]="{ specialties: ['Physiotherapy'], condition: 'exclude' }">
                <div class="list-content"><span>Lab Tests</span><mat-icon
                        *ngIf="useDynamicLayout && completedSections.labTests" class="green-icon">done</mat-icon>
                </div>
            </mat-list-item>

            <mat-list-item (click)="scrollTo('radiology_request')">
                <div class="list-content"><span>Request
                        For
                        Radiology</span>
                    <mat-icon *ngIf="useDynamicLayout && completedSections.radiologyRequest"
                        class="green-icon">done</mat-icon>
                </div>
            </mat-list-item>

            <mat-list-item (click)="scrollTo('tasks')">
                <div class="list-content"><span>Task
                        List</span><mat-icon *ngIf="useDynamicLayout && completedSections.tasks"
                        class="green-icon">done</mat-icon></div>
            </mat-list-item>
            <!--
              Always present, like every other nav entry. `scrollTo` calls `setVisibleSection`
              (:1564), so clicking this is what REVEALS the panel on a wide screen — which is why
              the link must not itself be gated on the panel having content. The `scrollTo` id
              matches the panel's `id` and `setVisibleSection`'s case.
            -->
            <mat-list-item (click)="scrollTo('riskManagement')">
                <div class="list-content"><span>Risk
                        Management</span><mat-icon *ngIf="useDynamicLayout && completedSections.riskManagement"
                        class="green-icon">done</mat-icon></div>
            </mat-list-item>
            <mat-list-item (click)="scrollTo('consent')">
                <div class="list-content"><span>Treatment
                        Consent</span>
                    <mat-icon *ngIf="useDynamicLayout && completedSections.consent" class="green-icon">done</mat-icon>
                </div>
            </mat-list-item>

            <mat-list-item (click)="scrollTo('lom')">
                <div class="list-content"><span>Letter
                        Of
                        Motivation</span>
                    <mat-icon *ngIf="useDynamicLayout && completedSections.lom" class="green-icon">done</mat-icon>
                </div>
            </mat-list-item>

            <mat-list-item (click)="scrollTo('letter_to_colleague')">
                <div class="list-content"><span>Letter
                        To
                        Colleague</span>
                    <mat-icon *ngIf="useDynamicLayout && completedSections.letterToColleague"
                        class="green-icon">done</mat-icon>
                </div>
            </mat-list-item>
            <mat-list-item (click)="scrollTo('letter_to_gp')">
                <div class="list-content"><span>Letter
                        To
                        {{selectedGroup?.groupSpeciality === 'General Practice' ? 'Specialist' :
                        'GP'}}</span>
                    <mat-icon *ngIf="useDynamicLayout && completedSections.letterToGP"
                        class="green-icon">done</mat-icon>
                </div>
            </mat-list-item>
            <mat-list-item (click)="scrollTo('letter_to_patient')">
                <div class="list-content"><span>Letter
                        To
                        Patient</span>
                    <mat-icon *ngIf="useDynamicLayout && completedSections.letterToPatient"
                        class="green-icon">done</mat-icon>
                </div>
            </mat-list-item>
            <mat-list-item (click)="scrollTo('letter_to_patient_employer')">
                <div class="list-content"><span>Sick
                        Letter</span>
                    <mat-icon *ngIf="useDynamicLayout && completedSections.letterToPatientWork"
                        class="green-icon">done</mat-icon>
                </div>
            </mat-list-item>
            <!--  <mat-list-item (click)="scrollTo('report')">
                <div class="list-content"><span>Patient Report</span>
                </div>
            </mat-list-item> -->
            <!--  <mat-list-item (click)="scrollTo('discharge_summary')">
                <div class="list-content"><span>Discharge Summary</span></div>
            </mat-list-item> -->
        </mat-nav-list>
    </div>
</div>

<ng-template #addNoteDialog>
    <h1 mat-dialog-title>Note Details</h1>
    <div mat-dialog-content>

        <div class="p-t-20 dialog-field-column">

            <mat-form-field appearance="outline">
                <mat-label>Date</mat-label>
                <input matInput [matDatepicker]="dateDatePicker" [(ngModel)]="addingNote.date" placeholder="DD/MM/YYYY"
                    [max]="todayDate">
                <mat-datepicker-toggle matSuffix [for]="dateDatePicker"></mat-datepicker-toggle>
                <mat-datepicker #dateDatePicker></mat-datepicker>
            </mat-form-field>

            <!-- <mat-form-field appearance="outline">
                <mat-label>Note Type</mat-label>
                <mat-select [(ngModel)]="addingNote.noteTypeId">
                    <mat-option *ngFor="let noteType of noteTypes" [value]="noteType.id">
                        {{ noteType.label }}
                    </mat-option>
                </mat-select>
            </mat-form-field> -->

            <mat-form-field appearance="outline">
                <mat-label>Note Type</mat-label>
                <mat-select [(ngModel)]="addingNote.noteTypeId" (selectionChange)="selectAddingNoteType($event)">

                    <!-- CONSULT Group -->
                    <mat-optgroup label="--- CONSULT NOTES ---">
                        <mat-option *ngFor="let noteType of consultNoteTypes" [value]="noteType.id">
                            {{ noteType.label }}
                        </mat-option>
                    </mat-optgroup>

                    <!-- PROCEDURE Group -->
                    <mat-optgroup label="--- PROCEDURE NOTES ---">
                        <mat-option *ngFor="let noteType of procedureNoteTypes" [value]="noteType.id">
                            {{ noteType.label }}
                        </mat-option>
                    </mat-optgroup>

                </mat-select>
            </mat-form-field>

            <mat-form-field appearance="outline">
                <mat-label>{{addingNote.isProcedure ? 'Procedure Title' : 'Note Title'}}</mat-label>
                <input matInput [(ngModel)]="addingNote.title">
            </mat-form-field>

            <!--   <div class="toggle-container">
                <mat-slide-toggle [(ngModel)]="addingNote.paidCash" (change)="toggleCodeStatus(addingNote, 'up-front')"
                    color="primary">Paid Up
                    Front</mat-slide-toggle>
                <mat-slide-toggle [(ngModel)]="addingNote.noCharge" color="primary"
                    (change)="toggleCodeStatus(addingNote, 'no-charge')" class="m-l-5">No
                    Charge</mat-slide-toggle>
            </div> -->

            <!-- Provider Selection -->
            <mat-form-field appearance="outline" *ngIf="showProviderSelection">
                <mat-label>Provider</mat-label>
                <mat-select [(ngModel)]="addingNote.providerId">
                    <mat-option *ngFor="let user of groupUsers" [value]="user.userId">
                        {{ user.title }} {{ user.name }} {{ user.surname }}
                    </mat-option>
                </mat-select>
            </mat-form-field>

            <!-- <app-procedure-code-selector [initialCodes]="addingNote.procedureCodes"
                (codesChanged)="onSelectedProcedureCodesChanged($event, addingNote)"
                [providedStartTime]="notesForm.get('startTime')?.value"
                [providedFinishTime]="notesForm.get('finishTime')?.value"
                [patient]="patient"></app-procedure-code-selector> -->

            <mat-form-field appearance="outline" class="m-t-20 full-width">
                <mat-label>Detected Coding Bundle</mat-label>
                <mat-select [(ngModel)]="addingNote.codingBundleId" (selectionChange)="selectCodingBundle($event)">
                    <mat-option *ngFor="let bundle of codingBundles" [value]="bundle.id">
                        {{ bundle.label }}
                    </mat-option>
                </mat-select>
            </mat-form-field>

            <!-- G3 (§6.2) — the theatre team as the DICTATION named them.
                 The transcript wins, but NEVER INVISIBLY: this block is what makes a swap away
                 from the booked assistant visible before the code is saved and billed. It is
                 shown only when there is something to say — a match that differs from the
                 booking, or a practice number that is missing. A confident match on the person
                 who was already booked is pre-selected silently (§6.2 row 1), and an ambiguous
                 or empty detection leaves the booking standing with no marker at all (row 4). -->
            <div class="theatre-team-marker" *ngIf="hasTheatreTeamMarker && !stateUnit">
                <mat-icon>record_voice_over</mat-icon>
                <div class="theatre-team-marker-body">
                    <ng-container *ngIf="theatreTeamPrefill?.assistant as assistant">
                        <p *ngIf="assistant.marker">
                            <strong>{{ assistant.name }}</strong> — {{ assistant.marker }}
                        </p>
                        <p class="flagged" *ngIf="assistant.practiceNumberFlagged && assistant.name">
                            <mat-icon inline="true">warning</mat-icon>
                            Practice number is empty. The assistant line cannot be claimed until it is filled in.
                        </p>
                        <button mat-stroked-button color="primary" *ngIf="assistant.offerAddToRoster"
                            (click)="addDetectedAssistantToRoster()"
                            matTooltip="Adds this person to Settings › MedicAI Network › Surgical Assistants">
                            <mat-icon>person_add</mat-icon> Add to Surgical Assistants
                        </button>
                    </ng-container>
                    <p *ngIf="theatreTeamPrefill?.anaesthetist?.marker">
                        <strong>{{ theatreTeamPrefill?.anaesthetist?.name }}</strong> —
                        {{ theatreTeamPrefill?.anaesthetist?.marker }}
                    </p>
                </div>
            </div>

            <!-- §3d — THE BOOKING COMPARISON, BEFORE THE SAVE.
                 The same server-side comparison the post-save banner renders, run while the
                 chips below are still editable. It warns and never blocks: the Save button is
                 untouched by this block, and a user who means to capture something that was not
                 booked simply saves. That is the whole posture — carry the codes through and
                 warn on the difference, do not enforce.

                 `inferred` means the booking was matched BY DATE rather than by a link, which
                 is workflow 3 (booked by hand, no letter of motivation). It is said out loud
                 because a user who can see how we reached a booking can tell us it is the wrong
                 one; nothing is written back off an inferred match. -->
            <div class="booking-comparison" *ngIf="bookingComparison as comparison">
                <mat-icon>rule</mat-icon>
                <div class="comparison-body">
                    <p class="comparison-title">
                        <strong>These codes differ from the theatre booking.</strong>
                        You can save anyway — this is a warning, not a block.
                    </p>
                    <p class="comparison-source" *ngIf="bookingComparisonIsInferred">
                        <mat-icon inline="true">info</mat-icon>
                        Matched to a theatre booking around this note's date. If that is the wrong
                        case, ignore this.
                    </p>
                    <p *ngIf="comparison.warnings?.capturedNotPlanned?.length">
                        <strong>Not on the booking:</strong>
                        <span *ngFor="let code of comparison.warnings.capturedNotPlanned; let last = last">
                            {{ code.code }}{{ code.description ? ' — ' + code.description : '' }}{{ last ? '' : ', ' }}
                        </span>
                    </p>
                    <p *ngIf="comparison.warnings?.plannedNotCaptured?.length">
                        <strong>On the booking but not here:</strong>
                        <span *ngFor="let code of comparison.warnings.plannedNotCaptured; let last = last">
                            {{ code.code }}{{ code.description ? ' — ' + code.description : '' }}{{ last ? '' : ', ' }}
                        </span>
                        <em> — the work was booked and authorised; check whether a line was missed.</em>
                    </p>
                </div>
            </div>

            <!--  <div class="icd-container"> -->
            <h3 *ngIf="!stateUnit">Selected Procedure Codes</h3>

            <div class="toggle-container" *ngIf="!stateUnit">
                <mat-slide-toggle [(ngModel)]="addingNote.paidCash" color="primary"
                    (change)="toggleCodeStatus(addingNote, 'up-front')">Paid
                    Up
                    Front</mat-slide-toggle>
                <mat-slide-toggle [(ngModel)]="addingNote.noCharge" color="primary"
                    (change)="toggleCodeStatus(addingNote, 'no-charge')" class="m-l-5">Pro Bono</mat-slide-toggle>
            </div>

            <app-procedure-code-selector #procedureCodeSelector [initialCodes]="addingNote.procedureCodes || []"
                (codesChanged)="onSelectedProcedureCodesChanged($event, addingNote)"
                [providedStartTime]="notesForm.get('startTime')?.value"
                [providedFinishTime]="notesForm.get('finishTime')?.value" [patient]="patient"
                [style.min-height]="'0 !important'" [style.height]="'auto !important'"></app-procedure-code-selector>
            <!--   </div> -->


            <div class="icd-container m-t-10" *ngIf="!stateUnit">
                <h4>Suggested Procedure Codes</h4>
                <mat-list class="full-width" *ngIf="addingNote.suggestedProcedureCodes?.codes">
                    <mat-list-item class="auto-height" *ngFor="let code of addingNote.suggestedProcedureCodes.codes">
                        <div class="icd-item">
                            <div class="icd-details">
                                <p matLine class="icd-title">{{code.code}} - {{code.description}}</p>
                                <p matLine class="icd-score">Relevance: {{code.score}}%</p>
                            </div>
                            <div class="view-icon">
                                <button mat-icon-button [matTooltip]="code.reason">
                                    <mat-icon>info</mat-icon>
                                </button>
                                <button mat-icon-button color="primary" matTooltip="Add this code to the patient file"
                                    (click)="addProcedureCode($event, code)">
                                    <mat-icon>library_add</mat-icon>
                                </button>
                            </div>
                        </div>
                    </mat-list-item>
                </mat-list>

                <div class="actions wrap">
                    <ng-container *ngIf="!addingNote.suggestedProcedureCodes?.codes?.length">
                        <button mat-raised-button class="ai-feature" (click)="aiGenerateProcedureCodeSuggestions()"
                            matTooltip="Generate a list of Procedure Code suggestions using MedicAI">
                            <mat-icon svgIcon="medic_ai"></mat-icon> Generate
                        </button>
                    </ng-container>
                </div>
            </div>

            <app-file-upload [fileObjects]="[]" [allowMultiple]="true" [allowedFileTypes]="'image/*, application/pdf'"
                title="Note Media" (fileUpload)="onNoteMediaUpload($event)" (fileDelete)="onNoteMediaDelete($event)"
                (filesEdited)="onFilesEdited($event)" [cameraEnabled]="true" [enableEditing]="true">
            </app-file-upload>

            <p class="hint center"><strong>Note: </strong>MedicAI can make mistakes. We advise that you check the
                content
                of the output carefully before accepting it into the patient file.</p>

        </div>

    </div>
    <div mat-dialog-actions class="dialog-actions">
        <button mat-button (click)="onCloseNoteDialog(null)">Cancel</button>
        <button mat-raised-button color="primary" [disabled]="!addingNote.date || !addingNote.title"
            (click)="onCloseNoteDialog(addingNote)">Save</button>
    </div>

</ng-template>
<ng-template #addColleagueDialog>
    <h1 mat-dialog-title>Colleague Details</h1>
    <div mat-dialog-content>
        <div class="p-t-20 field-column">
            <mat-form-field appearance="outline" class="full-width">
                <mat-label>Title</mat-label>
                <mat-select [(ngModel)]="addingColleague.title" required>
                    <mat-option *ngFor="let title of titles" [value]="title.value">
                        {{ title.text }}
                    </mat-option>
                </mat-select>
            </mat-form-field>
            <mat-form-field appearance="outline" class="full-width">
                <mat-label>Name</mat-label>
                <input matInput [(ngModel)]="addingColleague.name" placeholder="Name" type="text" required>
            </mat-form-field>
            <mat-form-field appearance="outline" class="full-width">
                <mat-label>Surname</mat-label>
                <input matInput [(ngModel)]="addingColleague.surname" placeholder="Surname" type="text" required>
            </mat-form-field>
            <mat-form-field appearance="outline" class="full-width">
                <mat-label>Speciality</mat-label>
                <mat-select [(ngModel)]="addingColleague.speciality">
                    <mat-option *ngFor="let speciality of specialities" [value]="speciality.value">
                        {{ speciality.text }}
                    </mat-option>
                </mat-select>
            </mat-form-field>
            <mat-form-field appearance="outline" class="full-width">
                <mat-label>Email</mat-label>
                <input matInput [(ngModel)]="addingColleague.email" placeholder="Email" type="email" required>
            </mat-form-field>
            <!-- <mat-form-field appearance="outline" class="full-width">
                <mat-label>Contact Number</mat-label>
                <input matInput [(ngModel)]="addingColleague.contactNumber" placeholder="Contact Number" type="tel">
            </mat-form-field> -->
            <app-intl-phone-input [preferredCountries]="['za']" [providedValue]="addingColleague.contactNumber"
                placeholder="Contact Number" (valueChange)="addingColleague.contactNumber = $event" name="contactNumber"
                classList="full-width"></app-intl-phone-input>
        </div>
    </div>
    <div mat-dialog-actions class="dialog-actions">
        <button mat-raised-button color="primary" [mat-dialog-close]="addingColleague">Save</button>
    </div>

</ng-template>

<ng-template #diagnosisIcdDialog>
    <h1 mat-dialog-title>Complete Patient Information</h1>
    <div mat-dialog-content>
        <div class="p-t-20 field-column">
            <div class="warning-banner">
                <mat-icon>warning</mat-icon>
                <p>Please complete the patient's diagnosis and ICD-10 codes before proceeding.</p>
            </div>

            <form [formGroup]="diagnosisIcdForm">
                <div class="field-with-action">
                    <mat-form-field appearance="outline">
                        <mat-label>Diagnosis/Active Problem</mat-label>
                        <textarea matInput cdkTextareaAutosize="true" formControlName="diagnosis"></textarea>
                    </mat-form-field>
                    <button mat-icon-button color="primary" matTooltip="Add additional diagnosis"
                        (click)="addDiagnosisFieldToDialog()" *ngIf="visibleDiagnosisFieldsDialog.length <= 24">
                        <mat-icon>add</mat-icon>
                    </button>
                </div>

                <ng-container *ngFor="let field of visibleDiagnosisFieldsDialog; let i = index">
                    <mat-form-field appearance="outline" class="full-width">
                        <mat-label>Diagnosis/Active Problem {{ i + 2 }}</mat-label>
                        <textarea matInput cdkTextareaAutosize="true" [formControlName]="field"></textarea>
                    </mat-form-field>
                </ng-container>

                <div class="icd-container">
                    <app-icd-code-selector [patientSex]="icdCodingSex" [patientAge]="icdCodingAge" [defaultOnsetDate]="icdDefaultOnsetDate" [initialCodes]="selectedPatientIcdCodesDialog"
                        (codesChanged)="onPatientIcdCodesDialogChanged($event)" [isRequired]="true"
                        #icdCodeSelectorDialog></app-icd-code-selector>
                </div>

                <div class="icd-container" *ngIf="notesForm.get('suggestedICD10Codes')?.value?.codes">
                    <h4>Suggested ICD-10 Codes</h4>
                    <mat-list class="full-width">
                        <mat-list-item class="auto-height"
                            *ngFor="let code of notesForm.get('suggestedICD10Codes')?.value?.codes">
                            <div class="icd-item">
                                <div class="icd-details">
                                    <p matLine class="icd-title">{{code.code}} - {{code.description}}</p>
                                    <p matLine class="icd-score">Relevance: {{code.score}}%</p>
                                </div>
                                <div class="view-icon">
                                    <button mat-icon-button [matTooltip]="code.reason">
                                        <mat-icon>info</mat-icon>
                                    </button>
                                    <button mat-icon-button color="primary"
                                        matTooltip="Add this code to the patient file"
                                        (click)="addICD10CodeToDialog($event, code)">
                                        <mat-icon>library_add</mat-icon>
                                    </button>
                                </div>
                            </div>
                        </mat-list-item>
                    </mat-list>
                </div>
            </form>
        </div>
    </div>
    <div mat-dialog-actions class="dialog-actions">
        <button mat-button (click)="onCloseDiagnosisIcdDialog(false)">Skip</button>
        <button mat-raised-button color="primary"
            [disabled]="diagnosisIcdForm.invalid || !selectedPatientIcdCodesDialog.length"
            (click)="onCloseDiagnosisIcdDialog(true)">Save & Continue</button>
    </div>
</ng-template>